// Web CRM (Business plane) Role-Dashboard i18n dictionary — STATIC UI CHROME ONLY.
// Merged into the flat per-language lookup in ./translations.js (same t() semantics).
// Covers the Analyst AnalyticsDashboard + DataHub, Executive ExecutiveStrategicDashboard,
// and Director StrategyDashboard visible static labels (tabs, KPI/metric labels, section
// headings, buttons, placeholders, chart legend/axis labels, and toast literals).
//
// SCOPE: static labels only. NEVER used for dynamic/backend/user content (metric values,
// counts, %, names, dates, API error text). English is the default + fallback. Every
// language MUST contain the same key set. Interpolation params ({days},{count},{rate},
// {name},{format},{status},{imported},{duplicates},{failed}) are values, never translated.

const roleDashTranslations = {
  en: {
    // Analytics dashboard
    'rd.loadingAnalytics': 'Loading analytics...',
    'rd.aiPerfSubtitle': 'AI performance analytics and insights',
    'rd.days7': '7 Days',
    'rd.days30': '30 Days',
    'rd.days90': '90 Days',
    'rd.exportData': 'Export Data',
    'rd.errLoadAnalytics': 'Failed to load analytics data',
    'rd.aiResolutionRate': 'Chat Auto-Resolution Rate',
    'rd.chatAutoResolved': 'Chat auto-resolved',
    'rd.avgResolutionTime': 'Avg Resolution Time',
    'rd.ticketCreateClose': 'Ticket create → close',
    'rd.totalTickets': 'Total Tickets',
    'rd.escalation': 'Escalation',
    'rd.openaiUsageRate': 'OpenAI Usage Rate',
    'rd.aiFallbackGpt': 'AI fallback to GPT',
    'rd.avgFirstResponse': 'Avg First Response',
    'rd.avgChatDuration': 'Avg Chat Duration',
    'rd.callbackRate': 'Callback Rate',
    'rd.reopenedConversations': 'Reopened Conversations',
    'rd.resolutionTrend': 'Resolution Trend',
    'rd.intentCoverage': 'Intent Coverage',
    'rd.forecast': 'Forecast',
    'rd.resolutionEscalationByDay': 'Resolution & Escalation Rate by Day',
    'rd.resolutionRateTrend': 'Resolution Rate Trend',
    'rd.resolutionPct': 'Resolution %',
    'rd.escalationPct': 'Escalation %',
    'rd.noTicketData': 'No ticket data for this period',
    'rd.chatConversations': 'Chat Conversations',
    'rd.autoResolved': 'Auto-Resolved',
    'rd.avgAiConfidence': 'Avg AI Confidence',
    'rd.solutionBankStatus': 'Solution Bank Status',
    'rd.solutionBankStats': 'Solution Bank Stats',
    'rd.total': 'Total',
    'rd.approved': 'Approved',
    'rd.pending': 'Pending',
    'rd.noSolutionBankData': 'No solution bank data',
    'rd.totalSolutions': 'Total Solutions',
    'rd.approvalRate': 'Approval Rate',
    'rd.intentDistributionChat': 'Intent Distribution (AI Chat)',
    'rd.intentDistribution': 'Intent Distribution',
    'rd.conversations': 'Conversations',
    'rd.noIntentData': 'No intent data available for this period',
    'rd.languageDistribution': 'Language Distribution',
    'rd.aiPredictionsForecasts': 'AI Predictions and Forecasts',
    'rd.aiResolutionForecast': 'AI Resolution Forecast (Next 30 Days)',
    'rd.currentResolutionRate': 'Current Resolution Rate',
    'rd.projectedRate5': 'Projected Rate (5% trend)',
    'rd.trendPerformingWell': 'Trend: Performing Well',
    'rd.trendNeedsImprovement': 'Trend: Needs Improvement',
    'rd.noDataYet': 'No data yet',
    'rd.basedOnLiveData': 'Based on {days}-day live data',
    'rd.ticketVolumeForecast': 'Ticket Volume Forecast',
    'rd.currentDayVolume': 'Current {days}-Day Volume',
    'rd.projectedMonthly': 'Projected Monthly',
    'rd.currentEscalationRate': 'Current Escalation Rate',
    'rd.basedOnLiveTicketData': 'Based on live ticket data',
    'rd.noTicketDataYet': 'No ticket data yet',
    'rd.projectedFromPeriodAvg': 'Projected from {days}-day period average',
    'rd.operationalInsights': 'Operational Insights',
    'rd.solutionBankReviewNeeded': 'Solution Bank Review Needed',
    'rd.pendingApprovalMsg': '{count} solution(s) pending approval — review to improve AI resolution rate',
    'rd.reopenedMsg': '{count} conversation(s) reopened — review resolution quality',
    'rd.highCallbackQueue': 'High Callback Queue',
    'rd.callbackRateMsg': 'callback rate — consider adding operator availability',
    'rd.expandSolutionBankCoverage': 'Expand Solution Bank Coverage',
    'rd.aiResolutionLowMsg': 'AI resolution at {rate}% — add approved solutions for top intents to improve coverage',
    'rd.strongAiPerformance': 'Strong AI Performance',
    'rd.aiResolvingMsg': 'AI resolving {rate}% of conversations — consider reviewing low-confidence escalations for KB improvement',
    'rd.loadAnalyticsToSeeInsights': 'Load analytics data to see insights',
    // Data Hub — domain names/types (display of the fixed tenant data collections)
    'rd.domain.conversations.name': 'Conversations',
    'rd.domain.conversations.type': 'Conversation history',
    'rd.domain.tickets.name': 'Tickets',
    'rd.domain.tickets.type': 'Support tickets',
    'rd.domain.contacts.name': 'Contacts',
    'rd.domain.contacts.type': 'Customer contacts',
    'rd.domain.surveys.name': 'Surveys',
    'rd.domain.surveys.type': 'Feedback surveys',
    // Data Hub — toasts
    'rd.synced': 'Synced',
    'rd.syncedDesc': '{name}: {count} records.',
    'rd.syncFailed': 'Sync failed',
    'rd.syncing': 'Syncing',
    'rd.syncingDesc': 'Refreshing all tenant data sources…',
    'rd.syncComplete': 'Sync complete',
    'rd.syncCompleteDesc': 'All data sources refreshed from live data.',
    'rd.nothingToExport': 'Nothing to export',
    'rd.noRecordsDesc': '{name} has no records.',
    'rd.source': 'Source',
    'rd.exportReady': 'Export ready',
    'rd.exportReadyDesc': '{name}: {count} records ({format}).',
    'rd.csvHeaderError': 'CSV must have a header row and at least one record.',
    'rd.importFailedStatus': 'Import failed ({status})',
    'rd.importComplete': 'Import complete',
    'rd.importCompleteDesc': '{imported} imported, {duplicates} duplicates, {failed} failed.',
    'rd.importFailed': 'Import failed',
    // Data Hub — body
    'rd.noWorkspace': 'Your account is not linked to a workspace yet, so tenant data cannot load. Contact your administrator if this is unexpected.',
    'rd.liveTenantData': 'Live tenant data',
    'rd.importing': 'Importing…',
    'rd.importData': 'Import Data',
    'rd.dataFilters': 'Data Filters',
    'rd.filterDataSources': 'Filter Data Sources',
    'rd.filterDataSourcesDesc': 'Narrow the live tenant data sources shown below.',
    'rd.dataSource': 'Data Source',
    'rd.allSources': 'All Sources',
    'rd.clear': 'Clear',
    'rd.apply': 'Apply',
    'rd.syncAll': 'Sync All',
    'rd.searchDataSources': 'Search data sources...',
    'rd.dataSources': 'Data Sources',
    'rd.withData': 'With Data',
    'rd.totalRecords': 'Total Records',
    'rd.avgCompleteness': 'Avg Completeness',
    'rd.aiVoiceAgentPerformance': 'AI Voice Agent Performance',
    'rd.loadingTenantData': 'Loading live tenant data…',
    'rd.noDataSourcesMatch': 'No data sources match your filter.',
    'rd.failedToLoad': 'Failed to load',
    'rd.badgeError': 'error',
    'rd.badgeLive': 'live',
    'rd.badgeEmpty': 'empty',
    'rd.recordsLabel': 'Records:',
    'rd.completenessLabel': 'Completeness:',
    'rd.latestLabel': 'Latest:',
    'rd.sync': 'Sync',
    'rd.export': 'Export',
    'rd.dataQuality': 'Data Quality',
    'rd.completeness': 'completeness',
    'rd.completenessNote': 'Completeness = share of records with the key fields populated (derived from live tenant data).',
    'rd.details': 'Details',
    'rd.exportDetails': 'Export Details',
    'rd.downloadRecords': 'Download {count} live records.',
    'rd.latestActivity': 'Latest activity:',
    'rd.channels': 'Channels',
    'rd.syncNow': 'Sync Now',
    // Executive / Strategy dashboards
    'rd.errLoadStrategic': 'Failed to load strategic data',
    'rd.operationalPerformance': 'Operational performance',
    'rd.yourBusiness': 'Your Business',
    'rd.totalCustomers': 'Total Customers',
    'rd.registeredInPlatform': 'Registered in platform',
    'rd.customerCsat': 'Customer CSAT',
    'rd.responsesCount': '{count} responses',
    'rd.noRatingsYet': 'No ratings yet',
    'rd.escalationRate': 'Escalation Rate',
    'rd.ticketEscalationRate': 'Ticket Escalation Rate',
    'rd.requiresHumanAgent': 'Requires human agent',
    'rd.operationalKpis': 'Operational KPIs',
    'rd.voiceChannel': 'Voice Channel',
    'rd.chatChannel': 'Chat Channel',
    'rd.serviceQuality': 'Service Quality',
    'rd.ticketVolumeResolutionTrend': 'Ticket Volume & Resolution Trend',
    'rd.inSelectedPeriod': 'In selected period',
    'rd.resolvedTickets': 'Resolved Tickets',
    'rd.closedSuccessfully': 'Closed successfully',
    'rd.reopened': 'Reopened',
    'rd.requiredFollowup': 'Required follow-up',
    'rd.createToClose': 'Create to close',
    'rd.firstReplyTime': 'First reply time',
    'rd.perResolvedConversation': 'Per resolved conversation',
    'rd.activeTickets': 'Active Tickets',
    'rd.openTickets': 'Open Tickets',
    'rd.openRightNow': 'Open right now',
    'rd.pendingCallbacks': 'Pending Callbacks',
    'rd.gptFallbackRate': 'GPT fallback rate',
    'rd.totalVoiceCalls': 'Total Voice Calls',
    'rd.aiHandled': 'AI Handled',
    'rd.aiResolved': 'AI Resolved',
    'rd.escalated': 'Escalated',
    'rd.tickets': 'Tickets',
    'rd.resolved': 'Resolved',
    'rd.avgCallDuration': 'Avg Call Duration',
    'rd.overdueCallbacks': 'Overdue Callbacks',
    'rd.callbackSlaBreaches': 'Callback SLA Breaches',
    'rd.noVoiceChannelData': 'No voice channel data available',
    'rd.totalChatSessions': 'Total Chat Sessions',
    'rd.resolutionRate': 'Resolution Rate',
    'rd.avgConfidence': 'Avg Confidence',
    'rd.topLanguage': 'Top Language',
    'rd.noChatData': 'No chat data available',
    'rd.csatScore': 'CSAT Score',
    'rd.basedOnRatings': 'Based on {count} customer ratings',
    'rd.noCustomerRatings': 'No customer ratings submitted yet',
    'rd.escalationCallbacks': 'Escalation & Callbacks',
    'rd.reopenedTickets': 'Reopened Tickets',
    'rd.operatorEscalationDistribution': 'Operator Escalation Distribution',
    'rd.escalationsCount': '{count} escalations',
    'rd.errLoadStrategy': 'Failed to load strategy data',
    'rd.operationalIntelligence': 'Operational intelligence',
    'rd.intentAnalysis': 'Intent Analysis',
    'rd.resolutionEscalationRate': 'Resolution & Escalation Rate',
    'rd.resolvedCount': '{count} resolved',
    'rd.customerIntentDistribution': 'Customer Intent Distribution (AI Chat)',
    'rd.noChatDataPeriod': 'No chat data for this period',
    'rd.escalationsPendingCallback': 'Escalations pending callback',
    'rd.openaiFallbackRate': 'OpenAI Fallback Rate',
    'rd.gptWhenNoMatch': 'AI used GPT when KB had no match',
    'rd.voiceTotalCalls': 'Voice Total Calls',
    'rd.allInboundVoice': 'All inbound AI voice sessions',
    'rd.voiceAiResolved': 'Voice AI Resolved',
    'rd.withoutHumanTransfer': 'Without human transfer',
    'rd.requiredReengagement': 'Required re-engagement after resolution',
    'rd.strategicMetricsNote': 'Strategic metrics (NPS, customer retention, market share, churn prediction, CLV segmentation) require survey integrations and CRM data enrichment. These cannot be derived from operational data alone.',
  },
  es: {
    'rd.loadingAnalytics': 'Cargando analíticas...',
    'rd.aiPerfSubtitle': 'Analíticas e información de rendimiento de la IA',
    'rd.days7': '7 días',
    'rd.days30': '30 días',
    'rd.days90': '90 días',
    'rd.exportData': 'Exportar datos',
    'rd.errLoadAnalytics': 'No se pudieron cargar los datos de analíticas',
    'rd.aiResolutionRate': 'Tasa de resolución por IA',
    'rd.chatAutoResolved': 'Chat autoresuelto',
    'rd.avgResolutionTime': 'Tiempo medio de resolución',
    'rd.ticketCreateClose': 'Creación → cierre del ticket',
    'rd.totalTickets': 'Total de tickets',
    'rd.escalation': 'Escalación',
    'rd.openaiUsageRate': 'Tasa de uso de OpenAI',
    'rd.aiFallbackGpt': 'Recurso de IA a GPT',
    'rd.avgFirstResponse': 'Primera respuesta media',
    'rd.avgChatDuration': 'Duración media del chat',
    'rd.callbackRate': 'Tasa de devolución de llamada',
    'rd.reopenedConversations': 'Conversaciones reabiertas',
    'rd.resolutionTrend': 'Tendencia de resolución',
    'rd.intentCoverage': 'Cobertura de intención',
    'rd.forecast': 'Pronóstico',
    'rd.resolutionEscalationByDay': 'Tasa de resolución y escalación por día',
    'rd.resolutionRateTrend': 'Tendencia de la tasa de resolución',
    'rd.resolutionPct': 'Resolución %',
    'rd.escalationPct': 'Escalación %',
    'rd.noTicketData': 'No hay datos de tickets para este período',
    'rd.chatConversations': 'Conversaciones de chat',
    'rd.autoResolved': 'Autoresuelto',
    'rd.avgAiConfidence': 'Confianza media de la IA',
    'rd.solutionBankStatus': 'Estado del banco de soluciones',
    'rd.solutionBankStats': 'Estadísticas del banco de soluciones',
    'rd.total': 'Total',
    'rd.approved': 'Aprobado',
    'rd.pending': 'Pendiente',
    'rd.noSolutionBankData': 'No hay datos del banco de soluciones',
    'rd.totalSolutions': 'Total de soluciones',
    'rd.approvalRate': 'Tasa de aprobación',
    'rd.intentDistributionChat': 'Distribución de intención (Chat IA)',
    'rd.intentDistribution': 'Distribución de intención',
    'rd.conversations': 'Conversaciones',
    'rd.noIntentData': 'No hay datos de intención disponibles para este período',
    'rd.languageDistribution': 'Distribución de idiomas',
    'rd.aiPredictionsForecasts': 'Predicciones y pronósticos de IA',
    'rd.aiResolutionForecast': 'Pronóstico de resolución por IA (próximos 30 días)',
    'rd.currentResolutionRate': 'Tasa de resolución actual',
    'rd.projectedRate5': 'Tasa proyectada (tendencia del 5%)',
    'rd.trendPerformingWell': 'Tendencia: buen rendimiento',
    'rd.trendNeedsImprovement': 'Tendencia: necesita mejorar',
    'rd.noDataYet': 'Aún no hay datos',
    'rd.basedOnLiveData': 'Basado en datos en vivo de {days} días',
    'rd.ticketVolumeForecast': 'Pronóstico de volumen de tickets',
    'rd.currentDayVolume': 'Volumen actual de {days} días',
    'rd.projectedMonthly': 'Proyección mensual',
    'rd.currentEscalationRate': 'Tasa de escalación actual',
    'rd.basedOnLiveTicketData': 'Basado en datos de tickets en vivo',
    'rd.noTicketDataYet': 'Aún no hay datos de tickets',
    'rd.projectedFromPeriodAvg': 'Proyectado a partir del promedio del período de {days} días',
    'rd.operationalInsights': 'Información operativa',
    'rd.solutionBankReviewNeeded': 'Se necesita revisar el banco de soluciones',
    'rd.pendingApprovalMsg': '{count} solución(es) pendiente(s) de aprobación — revisa para mejorar la tasa de resolución de la IA',
    'rd.reopenedMsg': '{count} conversación(es) reabierta(s) — revisa la calidad de la resolución',
    'rd.highCallbackQueue': 'Cola de devoluciones de llamada alta',
    'rd.callbackRateMsg': 'tasa de devolución de llamada — considera aumentar la disponibilidad de operadores',
    'rd.expandSolutionBankCoverage': 'Ampliar la cobertura del banco de soluciones',
    'rd.aiResolutionLowMsg': 'Resolución por IA al {rate}% — añade soluciones aprobadas para las principales intenciones para mejorar la cobertura',
    'rd.strongAiPerformance': 'Rendimiento sólido de la IA',
    'rd.aiResolvingMsg': 'La IA resuelve el {rate}% de las conversaciones — considera revisar las escalaciones de baja confianza para mejorar la base de conocimiento',
    'rd.loadAnalyticsToSeeInsights': 'Carga los datos de analíticas para ver la información',
    'rd.domain.conversations.name': 'Conversaciones',
    'rd.domain.conversations.type': 'Historial de conversaciones',
    'rd.domain.tickets.name': 'Tickets',
    'rd.domain.tickets.type': 'Tickets de soporte',
    'rd.domain.contacts.name': 'Contactos',
    'rd.domain.contacts.type': 'Contactos de clientes',
    'rd.domain.surveys.name': 'Encuestas',
    'rd.domain.surveys.type': 'Encuestas de opinión',
    'rd.synced': 'Sincronizado',
    'rd.syncedDesc': '{name}: {count} registros.',
    'rd.syncFailed': 'Error de sincronización',
    'rd.syncing': 'Sincronizando',
    'rd.syncingDesc': 'Actualizando todas las fuentes de datos del inquilino…',
    'rd.syncComplete': 'Sincronización completa',
    'rd.syncCompleteDesc': 'Todas las fuentes de datos se actualizaron con datos en vivo.',
    'rd.nothingToExport': 'Nada para exportar',
    'rd.noRecordsDesc': '{name} no tiene registros.',
    'rd.source': 'Fuente',
    'rd.exportReady': 'Exportación lista',
    'rd.exportReadyDesc': '{name}: {count} registros ({format}).',
    'rd.csvHeaderError': 'El CSV debe tener una fila de encabezado y al menos un registro.',
    'rd.importFailedStatus': 'Error en la importación ({status})',
    'rd.importComplete': 'Importación completa',
    'rd.importCompleteDesc': '{imported} importados, {duplicates} duplicados, {failed} fallidos.',
    'rd.importFailed': 'Error en la importación',
    'rd.noWorkspace': 'Tu cuenta aún no está vinculada a un espacio de trabajo, por lo que no se pueden cargar los datos del inquilino. Contacta a tu administrador si esto no es lo esperado.',
    'rd.liveTenantData': 'Datos del inquilino en vivo',
    'rd.importing': 'Importando…',
    'rd.importData': 'Importar datos',
    'rd.dataFilters': 'Filtros de datos',
    'rd.filterDataSources': 'Filtrar fuentes de datos',
    'rd.filterDataSourcesDesc': 'Acota las fuentes de datos del inquilino en vivo que se muestran abajo.',
    'rd.dataSource': 'Fuente de datos',
    'rd.allSources': 'Todas las fuentes',
    'rd.clear': 'Limpiar',
    'rd.apply': 'Aplicar',
    'rd.syncAll': 'Sincronizar todo',
    'rd.searchDataSources': 'Buscar fuentes de datos...',
    'rd.dataSources': 'Fuentes de datos',
    'rd.withData': 'Con datos',
    'rd.totalRecords': 'Total de registros',
    'rd.avgCompleteness': 'Integridad media',
    'rd.aiVoiceAgentPerformance': 'Rendimiento del agente de voz IA',
    'rd.loadingTenantData': 'Cargando datos del inquilino en vivo…',
    'rd.noDataSourcesMatch': 'Ninguna fuente de datos coincide con tu filtro.',
    'rd.failedToLoad': 'Error al cargar',
    'rd.badgeError': 'error',
    'rd.badgeLive': 'en vivo',
    'rd.badgeEmpty': 'vacío',
    'rd.recordsLabel': 'Registros:',
    'rd.completenessLabel': 'Integridad:',
    'rd.latestLabel': 'Último:',
    'rd.sync': 'Sincronizar',
    'rd.export': 'Exportar',
    'rd.dataQuality': 'Calidad de datos',
    'rd.completeness': 'integridad',
    'rd.completenessNote': 'Integridad = proporción de registros con los campos clave completos (derivado de datos del inquilino en vivo).',
    'rd.details': 'Detalles',
    'rd.exportDetails': 'Exportar detalles',
    'rd.downloadRecords': 'Descargar {count} registros en vivo.',
    'rd.latestActivity': 'Última actividad:',
    'rd.channels': 'Canales',
    'rd.syncNow': 'Sincronizar ahora',
    'rd.errLoadStrategic': 'No se pudieron cargar los datos estratégicos',
    'rd.operationalPerformance': 'Rendimiento operativo',
    'rd.yourBusiness': 'Tu empresa',
    'rd.totalCustomers': 'Total de clientes',
    'rd.registeredInPlatform': 'Registrados en la plataforma',
    'rd.customerCsat': 'CSAT del cliente',
    'rd.responsesCount': '{count} respuestas',
    'rd.noRatingsYet': 'Aún no hay valoraciones',
    'rd.escalationRate': 'Tasa de escalación',
    'rd.requiresHumanAgent': 'Requiere agente humano',
    'rd.operationalKpis': 'KPI operativos',
    'rd.voiceChannel': 'Canal de voz',
    'rd.chatChannel': 'Canal de chat',
    'rd.serviceQuality': 'Calidad del servicio',
    'rd.ticketVolumeResolutionTrend': 'Volumen de tickets y tendencia de resolución',
    'rd.inSelectedPeriod': 'En el período seleccionado',
    'rd.resolvedTickets': 'Tickets resueltos',
    'rd.closedSuccessfully': 'Cerrados con éxito',
    'rd.reopened': 'Reabiertos',
    'rd.requiredFollowup': 'Requirió seguimiento',
    'rd.createToClose': 'De creación a cierre',
    'rd.firstReplyTime': 'Tiempo de primera respuesta',
    'rd.perResolvedConversation': 'Por conversación resuelta',
    'rd.activeTickets': 'Tickets activos',
    'rd.openRightNow': 'Abiertos ahora mismo',
    'rd.pendingCallbacks': 'Devoluciones de llamada pendientes',
    'rd.gptFallbackRate': 'Tasa de recurso a GPT',
    'rd.totalVoiceCalls': 'Total de llamadas de voz',
    'rd.aiHandled': 'Atendidas por IA',
    'rd.aiResolved': 'Resueltas por IA',
    'rd.escalated': 'Escaladas',
    'rd.tickets': 'Tickets',
    'rd.resolved': 'Resueltos',
    'rd.avgCallDuration': 'Duración media de llamada',
    'rd.overdueCallbacks': 'Devoluciones de llamada vencidas',
    'rd.callbackSlaBreaches': 'Incumplimientos de SLA de devolución',
    'rd.noVoiceChannelData': 'No hay datos del canal de voz disponibles',
    'rd.totalChatSessions': 'Total de sesiones de chat',
    'rd.resolutionRate': 'Tasa de resolución',
    'rd.avgConfidence': 'Confianza media',
    'rd.topLanguage': 'Idioma principal',
    'rd.noChatData': 'No hay datos de chat disponibles',
    'rd.csatScore': 'Puntuación CSAT',
    'rd.basedOnRatings': 'Basado en {count} valoraciones de clientes',
    'rd.noCustomerRatings': 'Aún no se han enviado valoraciones de clientes',
    'rd.escalationCallbacks': 'Escalaciones y devoluciones de llamada',
    'rd.reopenedTickets': 'Tickets reabiertos',
    'rd.operatorEscalationDistribution': 'Distribución de escalaciones por operador',
    'rd.escalationsCount': '{count} escalaciones',
    'rd.errLoadStrategy': 'No se pudieron cargar los datos de estrategia',
    'rd.operationalIntelligence': 'Inteligencia operativa',
    'rd.intentAnalysis': 'Análisis de intención',
    'rd.resolutionEscalationRate': 'Tasa de resolución y escalación',
    'rd.resolvedCount': '{count} resueltos',
    'rd.customerIntentDistribution': 'Distribución de intención del cliente (Chat IA)',
    'rd.noChatDataPeriod': 'No hay datos de chat para este período',
    'rd.escalationsPendingCallback': 'Escalaciones pendientes de devolución de llamada',
    'rd.openaiFallbackRate': 'Tasa de recurso a OpenAI',
    'rd.gptWhenNoMatch': 'La IA usó GPT cuando la base de conocimiento no tuvo coincidencia',
    'rd.voiceTotalCalls': 'Total de llamadas de voz',
    'rd.allInboundVoice': 'Todas las sesiones de voz IA entrantes',
    'rd.voiceAiResolved': 'Voz resuelta por IA',
    'rd.withoutHumanTransfer': 'Sin transferencia a humano',
    'rd.requiredReengagement': 'Requirió reactivación tras la resolución',
    'rd.strategicMetricsNote': 'Las métricas estratégicas (NPS, retención de clientes, cuota de mercado, predicción de abandono, segmentación de CLV) requieren integraciones de encuestas y enriquecimiento de datos CRM. No pueden derivarse solo de datos operativos.',
  },
  fr: {
    'rd.loadingAnalytics': 'Chargement des analyses...',
    'rd.aiPerfSubtitle': "Analyses et informations sur les performances de l'IA",
    'rd.days7': '7 jours',
    'rd.days30': '30 jours',
    'rd.days90': '90 jours',
    'rd.exportData': 'Exporter les données',
    'rd.errLoadAnalytics': 'Échec du chargement des données analytiques',
    'rd.aiResolutionRate': 'Taux de résolution par IA',
    'rd.chatAutoResolved': 'Chat résolu automatiquement',
    'rd.avgResolutionTime': 'Temps de résolution moyen',
    'rd.ticketCreateClose': 'Création → clôture du ticket',
    'rd.totalTickets': 'Total des tickets',
    'rd.escalation': 'Escalade',
    'rd.openaiUsageRate': "Taux d'utilisation d'OpenAI",
    'rd.aiFallbackGpt': "Recours de l'IA à GPT",
    'rd.avgFirstResponse': 'Première réponse moyenne',
    'rd.avgChatDuration': 'Durée moyenne du chat',
    'rd.callbackRate': 'Taux de rappel',
    'rd.reopenedConversations': 'Conversations rouvertes',
    'rd.resolutionTrend': 'Tendance de résolution',
    'rd.intentCoverage': "Couverture des intentions",
    'rd.forecast': 'Prévision',
    'rd.resolutionEscalationByDay': "Taux de résolution et d'escalade par jour",
    'rd.resolutionRateTrend': 'Tendance du taux de résolution',
    'rd.resolutionPct': 'Résolution %',
    'rd.escalationPct': 'Escalade %',
    'rd.noTicketData': 'Aucune donnée de ticket pour cette période',
    'rd.chatConversations': 'Conversations de chat',
    'rd.autoResolved': 'Résolu automatiquement',
    'rd.avgAiConfidence': "Confiance moyenne de l'IA",
    'rd.solutionBankStatus': 'État de la banque de solutions',
    'rd.solutionBankStats': 'Statistiques de la banque de solutions',
    'rd.total': 'Total',
    'rd.approved': 'Approuvé',
    'rd.pending': 'En attente',
    'rd.noSolutionBankData': 'Aucune donnée de banque de solutions',
    'rd.totalSolutions': 'Total des solutions',
    'rd.approvalRate': "Taux d'approbation",
    'rd.intentDistributionChat': 'Répartition des intentions (Chat IA)',
    'rd.intentDistribution': 'Répartition des intentions',
    'rd.conversations': 'Conversations',
    'rd.noIntentData': "Aucune donnée d'intention disponible pour cette période",
    'rd.languageDistribution': 'Répartition des langues',
    'rd.aiPredictionsForecasts': "Prédictions et prévisions de l'IA",
    'rd.aiResolutionForecast': 'Prévision de résolution par IA (30 prochains jours)',
    'rd.currentResolutionRate': 'Taux de résolution actuel',
    'rd.projectedRate5': 'Taux projeté (tendance de 5 %)',
    'rd.trendPerformingWell': 'Tendance : bonnes performances',
    'rd.trendNeedsImprovement': 'Tendance : à améliorer',
    'rd.noDataYet': 'Pas encore de données',
    'rd.basedOnLiveData': 'Basé sur les données en direct de {days} jours',
    'rd.ticketVolumeForecast': 'Prévision du volume de tickets',
    'rd.currentDayVolume': 'Volume actuel sur {days} jours',
    'rd.projectedMonthly': 'Projection mensuelle',
    'rd.currentEscalationRate': "Taux d'escalade actuel",
    'rd.basedOnLiveTicketData': 'Basé sur les données de tickets en direct',
    'rd.noTicketDataYet': 'Pas encore de données de ticket',
    'rd.projectedFromPeriodAvg': 'Projeté à partir de la moyenne de la période de {days} jours',
    'rd.operationalInsights': 'Informations opérationnelles',
    'rd.solutionBankReviewNeeded': 'Révision de la banque de solutions nécessaire',
    'rd.pendingApprovalMsg': "{count} solution(s) en attente d'approbation — révisez pour améliorer le taux de résolution de l'IA",
    'rd.reopenedMsg': '{count} conversation(s) rouverte(s) — vérifiez la qualité de la résolution',
    'rd.highCallbackQueue': "File d'attente de rappels élevée",
    'rd.callbackRateMsg': "taux de rappel — envisagez d'augmenter la disponibilité des opérateurs",
    'rd.expandSolutionBankCoverage': 'Étendre la couverture de la banque de solutions',
    'rd.aiResolutionLowMsg': "Résolution par IA à {rate}% — ajoutez des solutions approuvées pour les principales intentions afin d'améliorer la couverture",
    'rd.strongAiPerformance': "Solides performances de l'IA",
    'rd.aiResolvingMsg': "L'IA résout {rate}% des conversations — envisagez de revoir les escalades à faible confiance pour améliorer la base de connaissances",
    'rd.loadAnalyticsToSeeInsights': 'Chargez les données analytiques pour voir les informations',
    'rd.domain.conversations.name': 'Conversations',
    'rd.domain.conversations.type': 'Historique des conversations',
    'rd.domain.tickets.name': 'Tickets',
    'rd.domain.tickets.type': 'Tickets de support',
    'rd.domain.contacts.name': 'Contacts',
    'rd.domain.contacts.type': 'Contacts clients',
    'rd.domain.surveys.name': 'Sondages',
    'rd.domain.surveys.type': 'Sondages de satisfaction',
    'rd.synced': 'Synchronisé',
    'rd.syncedDesc': '{name} : {count} enregistrements.',
    'rd.syncFailed': 'Échec de la synchronisation',
    'rd.syncing': 'Synchronisation',
    'rd.syncingDesc': 'Actualisation de toutes les sources de données du locataire…',
    'rd.syncComplete': 'Synchronisation terminée',
    'rd.syncCompleteDesc': 'Toutes les sources de données ont été actualisées avec des données en direct.',
    'rd.nothingToExport': 'Rien à exporter',
    'rd.noRecordsDesc': "{name} n'a aucun enregistrement.",
    'rd.source': 'Source',
    'rd.exportReady': 'Exportation prête',
    'rd.exportReadyDesc': '{name} : {count} enregistrements ({format}).',
    'rd.csvHeaderError': "Le CSV doit comporter une ligne d'en-tête et au moins un enregistrement.",
    'rd.importFailedStatus': "Échec de l'importation ({status})",
    'rd.importComplete': 'Importation terminée',
    'rd.importCompleteDesc': '{imported} importés, {duplicates} doublons, {failed} échoués.',
    'rd.importFailed': "Échec de l'importation",
    'rd.noWorkspace': "Votre compte n'est pas encore lié à un espace de travail, les données du locataire ne peuvent donc pas être chargées. Contactez votre administrateur si cela est inattendu.",
    'rd.liveTenantData': 'Données du locataire en direct',
    'rd.importing': 'Importation…',
    'rd.importData': 'Importer des données',
    'rd.dataFilters': 'Filtres de données',
    'rd.filterDataSources': 'Filtrer les sources de données',
    'rd.filterDataSourcesDesc': 'Affinez les sources de données du locataire en direct affichées ci-dessous.',
    'rd.dataSource': 'Source de données',
    'rd.allSources': 'Toutes les sources',
    'rd.clear': 'Effacer',
    'rd.apply': 'Appliquer',
    'rd.syncAll': 'Tout synchroniser',
    'rd.searchDataSources': 'Rechercher des sources de données...',
    'rd.dataSources': 'Sources de données',
    'rd.withData': 'Avec des données',
    'rd.totalRecords': "Total des enregistrements",
    'rd.avgCompleteness': 'Complétude moyenne',
    'rd.aiVoiceAgentPerformance': "Performances de l'agent vocal IA",
    'rd.loadingTenantData': 'Chargement des données du locataire en direct…',
    'rd.noDataSourcesMatch': 'Aucune source de données ne correspond à votre filtre.',
    'rd.failedToLoad': 'Échec du chargement',
    'rd.badgeError': 'erreur',
    'rd.badgeLive': 'en direct',
    'rd.badgeEmpty': 'vide',
    'rd.recordsLabel': 'Enregistrements :',
    'rd.completenessLabel': 'Complétude :',
    'rd.latestLabel': 'Dernier :',
    'rd.sync': 'Synchroniser',
    'rd.export': 'Exporter',
    'rd.dataQuality': 'Qualité des données',
    'rd.completeness': 'complétude',
    'rd.completenessNote': 'Complétude = part des enregistrements dont les champs clés sont renseignés (dérivée des données du locataire en direct).',
    'rd.details': 'Détails',
    'rd.exportDetails': 'Exporter les détails',
    'rd.downloadRecords': 'Télécharger {count} enregistrements en direct.',
    'rd.latestActivity': 'Dernière activité :',
    'rd.channels': 'Canaux',
    'rd.syncNow': 'Synchroniser maintenant',
    'rd.errLoadStrategic': 'Échec du chargement des données stratégiques',
    'rd.operationalPerformance': 'Performances opérationnelles',
    'rd.yourBusiness': 'Votre entreprise',
    'rd.totalCustomers': 'Total des clients',
    'rd.registeredInPlatform': 'Inscrits sur la plateforme',
    'rd.customerCsat': 'CSAT client',
    'rd.responsesCount': '{count} réponses',
    'rd.noRatingsYet': 'Pas encore de notes',
    'rd.escalationRate': "Taux d'escalade",
    'rd.requiresHumanAgent': 'Nécessite un agent humain',
    'rd.operationalKpis': 'KPI opérationnels',
    'rd.voiceChannel': 'Canal vocal',
    'rd.chatChannel': 'Canal de chat',
    'rd.serviceQuality': 'Qualité de service',
    'rd.ticketVolumeResolutionTrend': 'Volume de tickets et tendance de résolution',
    'rd.inSelectedPeriod': 'Dans la période sélectionnée',
    'rd.resolvedTickets': 'Tickets résolus',
    'rd.closedSuccessfully': 'Clôturés avec succès',
    'rd.reopened': 'Rouverts',
    'rd.requiredFollowup': 'A nécessité un suivi',
    'rd.createToClose': 'De la création à la clôture',
    'rd.firstReplyTime': 'Délai de première réponse',
    'rd.perResolvedConversation': 'Par conversation résolue',
    'rd.activeTickets': 'Tickets actifs',
    'rd.openRightNow': 'Ouverts en ce moment',
    'rd.pendingCallbacks': 'Rappels en attente',
    'rd.gptFallbackRate': 'Taux de recours à GPT',
    'rd.totalVoiceCalls': 'Total des appels vocaux',
    'rd.aiHandled': "Traités par l'IA",
    'rd.aiResolved': "Résolus par l'IA",
    'rd.escalated': 'Escaladés',
    'rd.tickets': 'Tickets',
    'rd.resolved': 'Résolus',
    'rd.avgCallDuration': "Durée moyenne d'appel",
    'rd.overdueCallbacks': 'Rappels en retard',
    'rd.callbackSlaBreaches': 'Violations de SLA de rappel',
    'rd.noVoiceChannelData': 'Aucune donnée du canal vocal disponible',
    'rd.totalChatSessions': 'Total des sessions de chat',
    'rd.resolutionRate': 'Taux de résolution',
    'rd.avgConfidence': 'Confiance moyenne',
    'rd.topLanguage': 'Langue principale',
    'rd.noChatData': 'Aucune donnée de chat disponible',
    'rd.csatScore': 'Score CSAT',
    'rd.basedOnRatings': 'Basé sur {count} évaluations de clients',
    'rd.noCustomerRatings': "Aucune évaluation de client n'a encore été soumise",
    'rd.escalationCallbacks': 'Escalades et rappels',
    'rd.reopenedTickets': 'Tickets rouverts',
    'rd.operatorEscalationDistribution': 'Répartition des escalades par opérateur',
    'rd.escalationsCount': '{count} escalades',
    'rd.errLoadStrategy': 'Échec du chargement des données de stratégie',
    'rd.operationalIntelligence': 'Intelligence opérationnelle',
    'rd.intentAnalysis': 'Analyse des intentions',
    'rd.resolutionEscalationRate': "Taux de résolution et d'escalade",
    'rd.resolvedCount': '{count} résolus',
    'rd.customerIntentDistribution': 'Répartition des intentions client (Chat IA)',
    'rd.noChatDataPeriod': 'Aucune donnée de chat pour cette période',
    'rd.escalationsPendingCallback': 'Escalades en attente de rappel',
    'rd.openaiFallbackRate': 'Taux de recours à OpenAI',
    'rd.gptWhenNoMatch': "L'IA a utilisé GPT quand la base de connaissances n'avait aucune correspondance",
    'rd.voiceTotalCalls': "Total des appels vocaux",
    'rd.allInboundVoice': 'Toutes les sessions vocales IA entrantes',
    'rd.voiceAiResolved': "Voix résolue par l'IA",
    'rd.withoutHumanTransfer': 'Sans transfert vers un humain',
    'rd.requiredReengagement': 'A nécessité une réactivation après résolution',
    'rd.strategicMetricsNote': "Les métriques stratégiques (NPS, fidélisation des clients, part de marché, prédiction de désabonnement, segmentation de la CLV) nécessitent des intégrations de sondages et un enrichissement des données CRM. Elles ne peuvent pas être dérivées des seules données opérationnelles.",
  },
  pt: {
    'rd.loadingAnalytics': 'Carregando análises...',
    'rd.aiPerfSubtitle': 'Análises e insights de desempenho da IA',
    'rd.days7': '7 dias',
    'rd.days30': '30 dias',
    'rd.days90': '90 dias',
    'rd.exportData': 'Exportar dados',
    'rd.errLoadAnalytics': 'Falha ao carregar os dados de análise',
    'rd.aiResolutionRate': 'Taxa de resolução por IA',
    'rd.chatAutoResolved': 'Chat resolvido automaticamente',
    'rd.avgResolutionTime': 'Tempo médio de resolução',
    'rd.ticketCreateClose': 'Criação → encerramento do ticket',
    'rd.totalTickets': 'Total de tickets',
    'rd.escalation': 'Escalação',
    'rd.openaiUsageRate': 'Taxa de uso da OpenAI',
    'rd.aiFallbackGpt': 'Recurso da IA ao GPT',
    'rd.avgFirstResponse': 'Primeira resposta média',
    'rd.avgChatDuration': 'Duração média do chat',
    'rd.callbackRate': 'Taxa de retorno de chamada',
    'rd.reopenedConversations': 'Conversas reabertas',
    'rd.resolutionTrend': 'Tendência de resolução',
    'rd.intentCoverage': 'Cobertura de intenção',
    'rd.forecast': 'Previsão',
    'rd.resolutionEscalationByDay': 'Taxa de resolução e escalação por dia',
    'rd.resolutionRateTrend': 'Tendência da taxa de resolução',
    'rd.resolutionPct': 'Resolução %',
    'rd.escalationPct': 'Escalação %',
    'rd.noTicketData': 'Sem dados de tickets para este período',
    'rd.chatConversations': 'Conversas de chat',
    'rd.autoResolved': 'Resolvido automaticamente',
    'rd.avgAiConfidence': 'Confiança média da IA',
    'rd.solutionBankStatus': 'Status do banco de soluções',
    'rd.solutionBankStats': 'Estatísticas do banco de soluções',
    'rd.total': 'Total',
    'rd.approved': 'Aprovado',
    'rd.pending': 'Pendente',
    'rd.noSolutionBankData': 'Sem dados do banco de soluções',
    'rd.totalSolutions': 'Total de soluções',
    'rd.approvalRate': 'Taxa de aprovação',
    'rd.intentDistributionChat': 'Distribuição de intenção (Chat IA)',
    'rd.intentDistribution': 'Distribuição de intenção',
    'rd.conversations': 'Conversas',
    'rd.noIntentData': 'Nenhum dado de intenção disponível para este período',
    'rd.languageDistribution': 'Distribuição de idiomas',
    'rd.aiPredictionsForecasts': 'Previsões e prognósticos de IA',
    'rd.aiResolutionForecast': 'Previsão de resolução por IA (próximos 30 dias)',
    'rd.currentResolutionRate': 'Taxa de resolução atual',
    'rd.projectedRate5': 'Taxa projetada (tendência de 5%)',
    'rd.trendPerformingWell': 'Tendência: bom desempenho',
    'rd.trendNeedsImprovement': 'Tendência: precisa melhorar',
    'rd.noDataYet': 'Ainda sem dados',
    'rd.basedOnLiveData': 'Com base em dados ao vivo de {days} dias',
    'rd.ticketVolumeForecast': 'Previsão de volume de tickets',
    'rd.currentDayVolume': 'Volume atual de {days} dias',
    'rd.projectedMonthly': 'Projeção mensal',
    'rd.currentEscalationRate': 'Taxa de escalação atual',
    'rd.basedOnLiveTicketData': 'Com base em dados de tickets ao vivo',
    'rd.noTicketDataYet': 'Ainda sem dados de tickets',
    'rd.projectedFromPeriodAvg': 'Projetado a partir da média do período de {days} dias',
    'rd.operationalInsights': 'Insights operacionais',
    'rd.solutionBankReviewNeeded': 'Revisão do banco de soluções necessária',
    'rd.pendingApprovalMsg': '{count} solução(ões) pendente(s) de aprovação — revise para melhorar a taxa de resolução da IA',
    'rd.reopenedMsg': '{count} conversa(s) reaberta(s) — revise a qualidade da resolução',
    'rd.highCallbackQueue': 'Fila de retornos de chamada alta',
    'rd.callbackRateMsg': 'taxa de retorno de chamada — considere aumentar a disponibilidade de operadores',
    'rd.expandSolutionBankCoverage': 'Ampliar a cobertura do banco de soluções',
    'rd.aiResolutionLowMsg': 'Resolução por IA em {rate}% — adicione soluções aprovadas para as principais intenções para melhorar a cobertura',
    'rd.strongAiPerformance': 'Desempenho forte da IA',
    'rd.aiResolvingMsg': 'A IA resolve {rate}% das conversas — considere revisar as escalações de baixa confiança para melhorar a base de conhecimento',
    'rd.loadAnalyticsToSeeInsights': 'Carregue os dados de análise para ver os insights',
    'rd.domain.conversations.name': 'Conversas',
    'rd.domain.conversations.type': 'Histórico de conversas',
    'rd.domain.tickets.name': 'Tickets',
    'rd.domain.tickets.type': 'Tickets de suporte',
    'rd.domain.contacts.name': 'Contatos',
    'rd.domain.contacts.type': 'Contatos de clientes',
    'rd.domain.surveys.name': 'Pesquisas',
    'rd.domain.surveys.type': 'Pesquisas de feedback',
    'rd.synced': 'Sincronizado',
    'rd.syncedDesc': '{name}: {count} registros.',
    'rd.syncFailed': 'Falha na sincronização',
    'rd.syncing': 'Sincronizando',
    'rd.syncingDesc': 'Atualizando todas as fontes de dados do inquilino…',
    'rd.syncComplete': 'Sincronização concluída',
    'rd.syncCompleteDesc': 'Todas as fontes de dados foram atualizadas com dados ao vivo.',
    'rd.nothingToExport': 'Nada para exportar',
    'rd.noRecordsDesc': '{name} não tem registros.',
    'rd.source': 'Fonte',
    'rd.exportReady': 'Exportação pronta',
    'rd.exportReadyDesc': '{name}: {count} registros ({format}).',
    'rd.csvHeaderError': 'O CSV deve ter uma linha de cabeçalho e pelo menos um registro.',
    'rd.importFailedStatus': 'Falha na importação ({status})',
    'rd.importComplete': 'Importação concluída',
    'rd.importCompleteDesc': '{imported} importados, {duplicates} duplicados, {failed} com falha.',
    'rd.importFailed': 'Falha na importação',
    'rd.noWorkspace': 'Sua conta ainda não está vinculada a um espaço de trabalho, portanto os dados do inquilino não podem ser carregados. Contate seu administrador se isso for inesperado.',
    'rd.liveTenantData': 'Dados do inquilino ao vivo',
    'rd.importing': 'Importando…',
    'rd.importData': 'Importar dados',
    'rd.dataFilters': 'Filtros de dados',
    'rd.filterDataSources': 'Filtrar fontes de dados',
    'rd.filterDataSourcesDesc': 'Restrinja as fontes de dados do inquilino ao vivo exibidas abaixo.',
    'rd.dataSource': 'Fonte de dados',
    'rd.allSources': 'Todas as fontes',
    'rd.clear': 'Limpar',
    'rd.apply': 'Aplicar',
    'rd.syncAll': 'Sincronizar tudo',
    'rd.searchDataSources': 'Pesquisar fontes de dados...',
    'rd.dataSources': 'Fontes de dados',
    'rd.withData': 'Com dados',
    'rd.totalRecords': 'Total de registros',
    'rd.avgCompleteness': 'Completude média',
    'rd.aiVoiceAgentPerformance': 'Desempenho do agente de voz IA',
    'rd.loadingTenantData': 'Carregando dados do inquilino ao vivo…',
    'rd.noDataSourcesMatch': 'Nenhuma fonte de dados corresponde ao seu filtro.',
    'rd.failedToLoad': 'Falha ao carregar',
    'rd.badgeError': 'erro',
    'rd.badgeLive': 'ao vivo',
    'rd.badgeEmpty': 'vazio',
    'rd.recordsLabel': 'Registros:',
    'rd.completenessLabel': 'Completude:',
    'rd.latestLabel': 'Mais recente:',
    'rd.sync': 'Sincronizar',
    'rd.export': 'Exportar',
    'rd.dataQuality': 'Qualidade dos dados',
    'rd.completeness': 'completude',
    'rd.completenessNote': 'Completude = proporção de registros com os campos-chave preenchidos (derivada dos dados do inquilino ao vivo).',
    'rd.details': 'Detalhes',
    'rd.exportDetails': 'Exportar detalhes',
    'rd.downloadRecords': 'Baixar {count} registros ao vivo.',
    'rd.latestActivity': 'Última atividade:',
    'rd.channels': 'Canais',
    'rd.syncNow': 'Sincronizar agora',
    'rd.errLoadStrategic': 'Falha ao carregar os dados estratégicos',
    'rd.operationalPerformance': 'Desempenho operacional',
    'rd.yourBusiness': 'Sua empresa',
    'rd.totalCustomers': 'Total de clientes',
    'rd.registeredInPlatform': 'Registrados na plataforma',
    'rd.customerCsat': 'CSAT do cliente',
    'rd.responsesCount': '{count} respostas',
    'rd.noRatingsYet': 'Ainda sem avaliações',
    'rd.escalationRate': 'Taxa de escalação',
    'rd.requiresHumanAgent': 'Requer agente humano',
    'rd.operationalKpis': 'KPIs operacionais',
    'rd.voiceChannel': 'Canal de voz',
    'rd.chatChannel': 'Canal de chat',
    'rd.serviceQuality': 'Qualidade do serviço',
    'rd.ticketVolumeResolutionTrend': 'Volume de tickets e tendência de resolução',
    'rd.inSelectedPeriod': 'No período selecionado',
    'rd.resolvedTickets': 'Tickets resolvidos',
    'rd.closedSuccessfully': 'Encerrados com sucesso',
    'rd.reopened': 'Reabertos',
    'rd.requiredFollowup': 'Exigiu acompanhamento',
    'rd.createToClose': 'Da criação ao encerramento',
    'rd.firstReplyTime': 'Tempo da primeira resposta',
    'rd.perResolvedConversation': 'Por conversa resolvida',
    'rd.activeTickets': 'Tickets ativos',
    'rd.openRightNow': 'Abertos agora',
    'rd.pendingCallbacks': 'Retornos de chamada pendentes',
    'rd.gptFallbackRate': 'Taxa de recurso ao GPT',
    'rd.totalVoiceCalls': 'Total de chamadas de voz',
    'rd.aiHandled': 'Atendidas pela IA',
    'rd.aiResolved': 'Resolvidas pela IA',
    'rd.escalated': 'Escaladas',
    'rd.tickets': 'Tickets',
    'rd.resolved': 'Resolvidos',
    'rd.avgCallDuration': 'Duração média da chamada',
    'rd.overdueCallbacks': 'Retornos de chamada atrasados',
    'rd.callbackSlaBreaches': 'Violações de SLA de retorno',
    'rd.noVoiceChannelData': 'Nenhum dado do canal de voz disponível',
    'rd.totalChatSessions': 'Total de sessões de chat',
    'rd.resolutionRate': 'Taxa de resolução',
    'rd.avgConfidence': 'Confiança média',
    'rd.topLanguage': 'Idioma principal',
    'rd.noChatData': 'Nenhum dado de chat disponível',
    'rd.csatScore': 'Pontuação CSAT',
    'rd.basedOnRatings': 'Com base em {count} avaliações de clientes',
    'rd.noCustomerRatings': 'Nenhuma avaliação de cliente enviada ainda',
    'rd.escalationCallbacks': 'Escalações e retornos de chamada',
    'rd.reopenedTickets': 'Tickets reabertos',
    'rd.operatorEscalationDistribution': 'Distribuição de escalações por operador',
    'rd.escalationsCount': '{count} escalações',
    'rd.errLoadStrategy': 'Falha ao carregar os dados de estratégia',
    'rd.operationalIntelligence': 'Inteligência operacional',
    'rd.intentAnalysis': 'Análise de intenção',
    'rd.resolutionEscalationRate': 'Taxa de resolução e escalação',
    'rd.resolvedCount': '{count} resolvidos',
    'rd.customerIntentDistribution': 'Distribuição de intenção do cliente (Chat IA)',
    'rd.noChatDataPeriod': 'Sem dados de chat para este período',
    'rd.escalationsPendingCallback': 'Escalações pendentes de retorno de chamada',
    'rd.openaiFallbackRate': 'Taxa de recurso à OpenAI',
    'rd.gptWhenNoMatch': 'A IA usou GPT quando a base de conhecimento não teve correspondência',
    'rd.voiceTotalCalls': 'Total de chamadas de voz',
    'rd.allInboundVoice': 'Todas as sessões de voz IA recebidas',
    'rd.voiceAiResolved': 'Voz resolvida pela IA',
    'rd.withoutHumanTransfer': 'Sem transferência para humano',
    'rd.requiredReengagement': 'Exigiu reengajamento após a resolução',
    'rd.strategicMetricsNote': 'As métricas estratégicas (NPS, retenção de clientes, participação de mercado, previsão de rotatividade, segmentação de CLV) exigem integrações de pesquisas e enriquecimento de dados de CRM. Elas não podem ser derivadas apenas de dados operacionais.',
  },
  de: {
    'rd.loadingAnalytics': 'Analysen werden geladen...',
    'rd.aiPerfSubtitle': 'KI-Leistungsanalysen und Erkenntnisse',
    'rd.days7': '7 Tage',
    'rd.days30': '30 Tage',
    'rd.days90': '90 Tage',
    'rd.exportData': 'Daten exportieren',
    'rd.errLoadAnalytics': 'Analysedaten konnten nicht geladen werden',
    'rd.aiResolutionRate': 'KI-Lösungsrate',
    'rd.chatAutoResolved': 'Chat automatisch gelöst',
    'rd.avgResolutionTime': 'Durchschn. Lösungszeit',
    'rd.ticketCreateClose': 'Ticket erstellen → schließen',
    'rd.totalTickets': 'Tickets gesamt',
    'rd.escalation': 'Eskalation',
    'rd.openaiUsageRate': 'OpenAI-Nutzungsrate',
    'rd.aiFallbackGpt': 'KI-Rückgriff auf GPT',
    'rd.avgFirstResponse': 'Durchschn. Erstreaktion',
    'rd.avgChatDuration': 'Durchschn. Chatdauer',
    'rd.callbackRate': 'Rückrufrate',
    'rd.reopenedConversations': 'Wieder geöffnete Konversationen',
    'rd.resolutionTrend': 'Lösungstrend',
    'rd.intentCoverage': 'Intent-Abdeckung',
    'rd.forecast': 'Prognose',
    'rd.resolutionEscalationByDay': 'Lösungs- und Eskalationsrate pro Tag',
    'rd.resolutionRateTrend': 'Trend der Lösungsrate',
    'rd.resolutionPct': 'Lösung %',
    'rd.escalationPct': 'Eskalation %',
    'rd.noTicketData': 'Keine Ticketdaten für diesen Zeitraum',
    'rd.chatConversations': 'Chat-Konversationen',
    'rd.autoResolved': 'Automatisch gelöst',
    'rd.avgAiConfidence': 'Durchschn. KI-Konfidenz',
    'rd.solutionBankStatus': 'Status der Lösungsdatenbank',
    'rd.solutionBankStats': 'Statistiken der Lösungsdatenbank',
    'rd.total': 'Gesamt',
    'rd.approved': 'Genehmigt',
    'rd.pending': 'Ausstehend',
    'rd.noSolutionBankData': 'Keine Daten der Lösungsdatenbank',
    'rd.totalSolutions': 'Lösungen gesamt',
    'rd.approvalRate': 'Genehmigungsrate',
    'rd.intentDistributionChat': 'Intent-Verteilung (KI-Chat)',
    'rd.intentDistribution': 'Intent-Verteilung',
    'rd.conversations': 'Konversationen',
    'rd.noIntentData': 'Keine Intent-Daten für diesen Zeitraum verfügbar',
    'rd.languageDistribution': 'Sprachverteilung',
    'rd.aiPredictionsForecasts': 'KI-Vorhersagen und -Prognosen',
    'rd.aiResolutionForecast': 'KI-Lösungsprognose (nächste 30 Tage)',
    'rd.currentResolutionRate': 'Aktuelle Lösungsrate',
    'rd.projectedRate5': 'Prognostizierte Rate (5%-Trend)',
    'rd.trendPerformingWell': 'Trend: Gute Leistung',
    'rd.trendNeedsImprovement': 'Trend: Verbesserung nötig',
    'rd.noDataYet': 'Noch keine Daten',
    'rd.basedOnLiveData': 'Basierend auf Live-Daten von {days} Tagen',
    'rd.ticketVolumeForecast': 'Prognose des Ticketvolumens',
    'rd.currentDayVolume': 'Aktuelles Volumen ({days} Tage)',
    'rd.projectedMonthly': 'Monatliche Prognose',
    'rd.currentEscalationRate': 'Aktuelle Eskalationsrate',
    'rd.basedOnLiveTicketData': 'Basierend auf Live-Ticketdaten',
    'rd.noTicketDataYet': 'Noch keine Ticketdaten',
    'rd.projectedFromPeriodAvg': 'Prognostiziert aus dem Durchschnitt des {days}-Tage-Zeitraums',
    'rd.operationalInsights': 'Betriebliche Erkenntnisse',
    'rd.solutionBankReviewNeeded': 'Überprüfung der Lösungsdatenbank erforderlich',
    'rd.pendingApprovalMsg': '{count} Lösung(en) zur Genehmigung ausstehend — prüfen, um die KI-Lösungsrate zu verbessern',
    'rd.reopenedMsg': '{count} wieder geöffnete Konversation(en) — Lösungsqualität überprüfen',
    'rd.highCallbackQueue': 'Hohe Rückrufwarteschlange',
    'rd.callbackRateMsg': 'Rückrufrate — erwägen Sie, die Verfügbarkeit von Bedienern zu erhöhen',
    'rd.expandSolutionBankCoverage': 'Abdeckung der Lösungsdatenbank erweitern',
    'rd.aiResolutionLowMsg': 'KI-Lösung bei {rate}% — fügen Sie genehmigte Lösungen für die wichtigsten Intents hinzu, um die Abdeckung zu verbessern',
    'rd.strongAiPerformance': 'Starke KI-Leistung',
    'rd.aiResolvingMsg': 'KI löst {rate}% der Konversationen — erwägen Sie, Eskalationen mit geringer Konfidenz zur Verbesserung der Wissensdatenbank zu prüfen',
    'rd.loadAnalyticsToSeeInsights': 'Laden Sie Analysedaten, um Erkenntnisse zu sehen',
    'rd.domain.conversations.name': 'Konversationen',
    'rd.domain.conversations.type': 'Konversationsverlauf',
    'rd.domain.tickets.name': 'Tickets',
    'rd.domain.tickets.type': 'Support-Tickets',
    'rd.domain.contacts.name': 'Kontakte',
    'rd.domain.contacts.type': 'Kundenkontakte',
    'rd.domain.surveys.name': 'Umfragen',
    'rd.domain.surveys.type': 'Feedback-Umfragen',
    'rd.synced': 'Synchronisiert',
    'rd.syncedDesc': '{name}: {count} Datensätze.',
    'rd.syncFailed': 'Synchronisierung fehlgeschlagen',
    'rd.syncing': 'Synchronisierung',
    'rd.syncingDesc': 'Alle Mandanten-Datenquellen werden aktualisiert…',
    'rd.syncComplete': 'Synchronisierung abgeschlossen',
    'rd.syncCompleteDesc': 'Alle Datenquellen wurden mit Live-Daten aktualisiert.',
    'rd.nothingToExport': 'Nichts zu exportieren',
    'rd.noRecordsDesc': '{name} hat keine Datensätze.',
    'rd.source': 'Quelle',
    'rd.exportReady': 'Export bereit',
    'rd.exportReadyDesc': '{name}: {count} Datensätze ({format}).',
    'rd.csvHeaderError': 'Die CSV-Datei muss eine Kopfzeile und mindestens einen Datensatz enthalten.',
    'rd.importFailedStatus': 'Import fehlgeschlagen ({status})',
    'rd.importComplete': 'Import abgeschlossen',
    'rd.importCompleteDesc': '{imported} importiert, {duplicates} Duplikate, {failed} fehlgeschlagen.',
    'rd.importFailed': 'Import fehlgeschlagen',
    'rd.noWorkspace': 'Ihr Konto ist noch keinem Arbeitsbereich zugeordnet, daher können keine Mandantendaten geladen werden. Wenden Sie sich an Ihren Administrator, falls dies unerwartet ist.',
    'rd.liveTenantData': 'Live-Mandantendaten',
    'rd.importing': 'Importieren…',
    'rd.importData': 'Daten importieren',
    'rd.dataFilters': 'Datenfilter',
    'rd.filterDataSources': 'Datenquellen filtern',
    'rd.filterDataSourcesDesc': 'Schränken Sie die unten angezeigten Live-Mandanten-Datenquellen ein.',
    'rd.dataSource': 'Datenquelle',
    'rd.allSources': 'Alle Quellen',
    'rd.clear': 'Löschen',
    'rd.apply': 'Anwenden',
    'rd.syncAll': 'Alle synchronisieren',
    'rd.searchDataSources': 'Datenquellen suchen...',
    'rd.dataSources': 'Datenquellen',
    'rd.withData': 'Mit Daten',
    'rd.totalRecords': 'Datensätze gesamt',
    'rd.avgCompleteness': 'Durchschn. Vollständigkeit',
    'rd.aiVoiceAgentPerformance': 'Leistung des KI-Sprachagenten',
    'rd.loadingTenantData': 'Live-Mandantendaten werden geladen…',
    'rd.noDataSourcesMatch': 'Keine Datenquellen entsprechen Ihrem Filter.',
    'rd.failedToLoad': 'Laden fehlgeschlagen',
    'rd.badgeError': 'Fehler',
    'rd.badgeLive': 'live',
    'rd.badgeEmpty': 'leer',
    'rd.recordsLabel': 'Datensätze:',
    'rd.completenessLabel': 'Vollständigkeit:',
    'rd.latestLabel': 'Neueste:',
    'rd.sync': 'Synchronisieren',
    'rd.export': 'Exportieren',
    'rd.dataQuality': 'Datenqualität',
    'rd.completeness': 'Vollständigkeit',
    'rd.completenessNote': 'Vollständigkeit = Anteil der Datensätze mit ausgefüllten Schlüsselfeldern (abgeleitet aus Live-Mandantendaten).',
    'rd.details': 'Details',
    'rd.exportDetails': 'Details exportieren',
    'rd.downloadRecords': '{count} Live-Datensätze herunterladen.',
    'rd.latestActivity': 'Letzte Aktivität:',
    'rd.channels': 'Kanäle',
    'rd.syncNow': 'Jetzt synchronisieren',
    'rd.errLoadStrategic': 'Strategische Daten konnten nicht geladen werden',
    'rd.operationalPerformance': 'Betriebsleistung',
    'rd.yourBusiness': 'Ihr Unternehmen',
    'rd.totalCustomers': 'Kunden gesamt',
    'rd.registeredInPlatform': 'In der Plattform registriert',
    'rd.customerCsat': 'Kunden-CSAT',
    'rd.responsesCount': '{count} Antworten',
    'rd.noRatingsYet': 'Noch keine Bewertungen',
    'rd.escalationRate': 'Eskalationsrate',
    'rd.requiresHumanAgent': 'Erfordert menschlichen Agenten',
    'rd.operationalKpis': 'Operative KPIs',
    'rd.voiceChannel': 'Sprachkanal',
    'rd.chatChannel': 'Chat-Kanal',
    'rd.serviceQuality': 'Servicequalität',
    'rd.ticketVolumeResolutionTrend': 'Ticketvolumen- und Lösungstrend',
    'rd.inSelectedPeriod': 'Im ausgewählten Zeitraum',
    'rd.resolvedTickets': 'Gelöste Tickets',
    'rd.closedSuccessfully': 'Erfolgreich geschlossen',
    'rd.reopened': 'Wieder geöffnet',
    'rd.requiredFollowup': 'Nachverfolgung erforderlich',
    'rd.createToClose': 'Von Erstellung bis Schließung',
    'rd.firstReplyTime': 'Zeit bis zur Erstantwort',
    'rd.perResolvedConversation': 'Pro gelöster Konversation',
    'rd.activeTickets': 'Aktive Tickets',
    'rd.openRightNow': 'Gerade offen',
    'rd.pendingCallbacks': 'Ausstehende Rückrufe',
    'rd.gptFallbackRate': 'GPT-Rückgriffsrate',
    'rd.totalVoiceCalls': 'Sprachanrufe gesamt',
    'rd.aiHandled': 'Von KI bearbeitet',
    'rd.aiResolved': 'Von KI gelöst',
    'rd.escalated': 'Eskaliert',
    'rd.tickets': 'Tickets',
    'rd.resolved': 'Gelöst',
    'rd.avgCallDuration': 'Durchschn. Anrufdauer',
    'rd.overdueCallbacks': 'Überfällige Rückrufe',
    'rd.callbackSlaBreaches': 'Rückruf-SLA-Verstöße',
    'rd.noVoiceChannelData': 'Keine Daten zum Sprachkanal verfügbar',
    'rd.totalChatSessions': 'Chat-Sitzungen gesamt',
    'rd.resolutionRate': 'Lösungsrate',
    'rd.avgConfidence': 'Durchschn. Konfidenz',
    'rd.topLanguage': 'Häufigste Sprache',
    'rd.noChatData': 'Keine Chat-Daten verfügbar',
    'rd.csatScore': 'CSAT-Wert',
    'rd.basedOnRatings': 'Basierend auf {count} Kundenbewertungen',
    'rd.noCustomerRatings': 'Noch keine Kundenbewertungen eingereicht',
    'rd.escalationCallbacks': 'Eskalationen und Rückrufe',
    'rd.reopenedTickets': 'Wieder geöffnete Tickets',
    'rd.operatorEscalationDistribution': 'Verteilung der Eskalationen nach Bediener',
    'rd.escalationsCount': '{count} Eskalationen',
    'rd.errLoadStrategy': 'Strategiedaten konnten nicht geladen werden',
    'rd.operationalIntelligence': 'Operative Intelligenz',
    'rd.intentAnalysis': 'Intent-Analyse',
    'rd.resolutionEscalationRate': 'Lösungs- und Eskalationsrate',
    'rd.resolvedCount': '{count} gelöst',
    'rd.customerIntentDistribution': 'Verteilung der Kundenabsichten (KI-Chat)',
    'rd.noChatDataPeriod': 'Keine Chat-Daten für diesen Zeitraum',
    'rd.escalationsPendingCallback': 'Eskalationen mit ausstehendem Rückruf',
    'rd.openaiFallbackRate': 'OpenAI-Rückgriffsrate',
    'rd.gptWhenNoMatch': 'KI nutzte GPT, als die Wissensdatenbank keine Übereinstimmung hatte',
    'rd.voiceTotalCalls': 'Sprachanrufe gesamt',
    'rd.allInboundVoice': 'Alle eingehenden KI-Sprachsitzungen',
    'rd.voiceAiResolved': 'Von KI gelöste Sprache',
    'rd.withoutHumanTransfer': 'Ohne menschliche Weiterleitung',
    'rd.requiredReengagement': 'Erneute Kontaktaufnahme nach Lösung erforderlich',
    'rd.strategicMetricsNote': 'Strategische Kennzahlen (NPS, Kundenbindung, Marktanteil, Abwanderungsprognose, CLV-Segmentierung) erfordern Umfrageintegrationen und die Anreicherung von CRM-Daten. Sie können nicht allein aus Betriebsdaten abgeleitet werden.',
  },
  it: {
    'rd.loadingAnalytics': 'Caricamento analisi...',
    'rd.aiPerfSubtitle': "Analisi e approfondimenti sulle prestazioni dell'IA",
    'rd.days7': '7 giorni',
    'rd.days30': '30 giorni',
    'rd.days90': '90 giorni',
    'rd.exportData': 'Esporta dati',
    'rd.errLoadAnalytics': 'Impossibile caricare i dati di analisi',
    'rd.aiResolutionRate': "Tasso di risoluzione dell'IA",
    'rd.chatAutoResolved': 'Chat risolta automaticamente',
    'rd.avgResolutionTime': 'Tempo medio di risoluzione',
    'rd.ticketCreateClose': 'Creazione → chiusura del ticket',
    'rd.totalTickets': 'Ticket totali',
    'rd.escalation': 'Escalation',
    'rd.openaiUsageRate': 'Tasso di utilizzo di OpenAI',
    'rd.aiFallbackGpt': 'Ricorso IA a GPT',
    'rd.avgFirstResponse': 'Prima risposta media',
    'rd.avgChatDuration': 'Durata media della chat',
    'rd.callbackRate': 'Tasso di richiamata',
    'rd.reopenedConversations': 'Conversazioni riaperte',
    'rd.resolutionTrend': 'Andamento della risoluzione',
    'rd.intentCoverage': 'Copertura degli intenti',
    'rd.forecast': 'Previsione',
    'rd.resolutionEscalationByDay': 'Tasso di risoluzione ed escalation per giorno',
    'rd.resolutionRateTrend': 'Andamento del tasso di risoluzione',
    'rd.resolutionPct': 'Risoluzione %',
    'rd.escalationPct': 'Escalation %',
    'rd.noTicketData': 'Nessun dato sui ticket per questo periodo',
    'rd.chatConversations': 'Conversazioni chat',
    'rd.autoResolved': 'Risolto automaticamente',
    'rd.avgAiConfidence': "Confidenza media dell'IA",
    'rd.solutionBankStatus': 'Stato della banca delle soluzioni',
    'rd.solutionBankStats': 'Statistiche della banca delle soluzioni',
    'rd.total': 'Totale',
    'rd.approved': 'Approvato',
    'rd.pending': 'In attesa',
    'rd.noSolutionBankData': 'Nessun dato della banca delle soluzioni',
    'rd.totalSolutions': 'Soluzioni totali',
    'rd.approvalRate': 'Tasso di approvazione',
    'rd.intentDistributionChat': 'Distribuzione degli intenti (Chat IA)',
    'rd.intentDistribution': 'Distribuzione degli intenti',
    'rd.conversations': 'Conversazioni',
    'rd.noIntentData': 'Nessun dato sugli intenti disponibile per questo periodo',
    'rd.languageDistribution': 'Distribuzione delle lingue',
    'rd.aiPredictionsForecasts': "Previsioni e proiezioni dell'IA",
    'rd.aiResolutionForecast': "Previsione di risoluzione dell'IA (prossimi 30 giorni)",
    'rd.currentResolutionRate': 'Tasso di risoluzione attuale',
    'rd.projectedRate5': 'Tasso previsto (tendenza del 5%)',
    'rd.trendPerformingWell': 'Tendenza: buone prestazioni',
    'rd.trendNeedsImprovement': 'Tendenza: da migliorare',
    'rd.noDataYet': 'Ancora nessun dato',
    'rd.basedOnLiveData': 'Basato su dati in tempo reale di {days} giorni',
    'rd.ticketVolumeForecast': 'Previsione del volume di ticket',
    'rd.currentDayVolume': 'Volume attuale di {days} giorni',
    'rd.projectedMonthly': 'Proiezione mensile',
    'rd.currentEscalationRate': 'Tasso di escalation attuale',
    'rd.basedOnLiveTicketData': 'Basato su dati dei ticket in tempo reale',
    'rd.noTicketDataYet': 'Ancora nessun dato sui ticket',
    'rd.projectedFromPeriodAvg': 'Proiettato dalla media del periodo di {days} giorni',
    'rd.operationalInsights': 'Approfondimenti operativi',
    'rd.solutionBankReviewNeeded': 'Revisione della banca delle soluzioni necessaria',
    'rd.pendingApprovalMsg': "{count} soluzione/i in attesa di approvazione — rivedi per migliorare il tasso di risoluzione dell'IA",
    'rd.reopenedMsg': '{count} conversazione/i riaperta/e — rivedi la qualità della risoluzione',
    'rd.highCallbackQueue': 'Coda di richiamate elevata',
    'rd.callbackRateMsg': 'tasso di richiamata — valuta di aumentare la disponibilità degli operatori',
    'rd.expandSolutionBankCoverage': 'Amplia la copertura della banca delle soluzioni',
    'rd.aiResolutionLowMsg': "Risoluzione IA al {rate}% — aggiungi soluzioni approvate per gli intenti principali per migliorare la copertura",
    'rd.strongAiPerformance': "Ottime prestazioni dell'IA",
    'rd.aiResolvingMsg': "L'IA risolve il {rate}% delle conversazioni — valuta di rivedere le escalation a bassa confidenza per migliorare la base di conoscenza",
    'rd.loadAnalyticsToSeeInsights': 'Carica i dati di analisi per vedere gli approfondimenti',
    'rd.domain.conversations.name': 'Conversazioni',
    'rd.domain.conversations.type': 'Cronologia delle conversazioni',
    'rd.domain.tickets.name': 'Ticket',
    'rd.domain.tickets.type': 'Ticket di assistenza',
    'rd.domain.contacts.name': 'Contatti',
    'rd.domain.contacts.type': 'Contatti clienti',
    'rd.domain.surveys.name': 'Sondaggi',
    'rd.domain.surveys.type': 'Sondaggi di feedback',
    'rd.synced': 'Sincronizzato',
    'rd.syncedDesc': '{name}: {count} record.',
    'rd.syncFailed': 'Sincronizzazione non riuscita',
    'rd.syncing': 'Sincronizzazione',
    'rd.syncingDesc': 'Aggiornamento di tutte le fonti dati del tenant…',
    'rd.syncComplete': 'Sincronizzazione completata',
    'rd.syncCompleteDesc': 'Tutte le fonti dati sono state aggiornate con dati in tempo reale.',
    'rd.nothingToExport': 'Niente da esportare',
    'rd.noRecordsDesc': '{name} non ha record.',
    'rd.source': 'Fonte',
    'rd.exportReady': 'Esportazione pronta',
    'rd.exportReadyDesc': '{name}: {count} record ({format}).',
    'rd.csvHeaderError': 'Il CSV deve avere una riga di intestazione e almeno un record.',
    'rd.importFailedStatus': 'Importazione non riuscita ({status})',
    'rd.importComplete': 'Importazione completata',
    'rd.importCompleteDesc': '{imported} importati, {duplicates} duplicati, {failed} non riusciti.',
    'rd.importFailed': 'Importazione non riuscita',
    'rd.noWorkspace': "Il tuo account non è ancora collegato a un'area di lavoro, quindi i dati del tenant non possono essere caricati. Contatta il tuo amministratore se è inaspettato.",
    'rd.liveTenantData': 'Dati del tenant in tempo reale',
    'rd.importing': 'Importazione…',
    'rd.importData': 'Importa dati',
    'rd.dataFilters': 'Filtri dati',
    'rd.filterDataSources': 'Filtra le fonti dati',
    'rd.filterDataSourcesDesc': 'Restringi le fonti dati del tenant in tempo reale mostrate di seguito.',
    'rd.dataSource': 'Fonte dati',
    'rd.allSources': 'Tutte le fonti',
    'rd.clear': 'Cancella',
    'rd.apply': 'Applica',
    'rd.syncAll': 'Sincronizza tutto',
    'rd.searchDataSources': 'Cerca fonti dati...',
    'rd.dataSources': 'Fonti dati',
    'rd.withData': 'Con dati',
    'rd.totalRecords': 'Record totali',
    'rd.avgCompleteness': 'Completezza media',
    'rd.aiVoiceAgentPerformance': "Prestazioni dell'agente vocale IA",
    'rd.loadingTenantData': 'Caricamento dati del tenant in tempo reale…',
    'rd.noDataSourcesMatch': 'Nessuna fonte dati corrisponde al tuo filtro.',
    'rd.failedToLoad': 'Caricamento non riuscito',
    'rd.badgeError': 'errore',
    'rd.badgeLive': 'live',
    'rd.badgeEmpty': 'vuoto',
    'rd.recordsLabel': 'Record:',
    'rd.completenessLabel': 'Completezza:',
    'rd.latestLabel': 'Ultimo:',
    'rd.sync': 'Sincronizza',
    'rd.export': 'Esporta',
    'rd.dataQuality': 'Qualità dei dati',
    'rd.completeness': 'completezza',
    'rd.completenessNote': 'Completezza = quota di record con i campi chiave compilati (derivata dai dati del tenant in tempo reale).',
    'rd.details': 'Dettagli',
    'rd.exportDetails': 'Esporta dettagli',
    'rd.downloadRecords': 'Scarica {count} record in tempo reale.',
    'rd.latestActivity': 'Ultima attività:',
    'rd.channels': 'Canali',
    'rd.syncNow': 'Sincronizza ora',
    'rd.errLoadStrategic': 'Impossibile caricare i dati strategici',
    'rd.operationalPerformance': 'Prestazioni operative',
    'rd.yourBusiness': 'La tua azienda',
    'rd.totalCustomers': 'Clienti totali',
    'rd.registeredInPlatform': 'Registrati sulla piattaforma',
    'rd.customerCsat': 'CSAT cliente',
    'rd.responsesCount': '{count} risposte',
    'rd.noRatingsYet': 'Ancora nessuna valutazione',
    'rd.escalationRate': 'Tasso di escalation',
    'rd.requiresHumanAgent': 'Richiede un agente umano',
    'rd.operationalKpis': 'KPI operativi',
    'rd.voiceChannel': 'Canale vocale',
    'rd.chatChannel': 'Canale chat',
    'rd.serviceQuality': 'Qualità del servizio',
    'rd.ticketVolumeResolutionTrend': 'Volume dei ticket e andamento della risoluzione',
    'rd.inSelectedPeriod': 'Nel periodo selezionato',
    'rd.resolvedTickets': 'Ticket risolti',
    'rd.closedSuccessfully': 'Chiusi con successo',
    'rd.reopened': 'Riaperti',
    'rd.requiredFollowup': 'Ha richiesto un follow-up',
    'rd.createToClose': 'Dalla creazione alla chiusura',
    'rd.firstReplyTime': 'Tempo di prima risposta',
    'rd.perResolvedConversation': 'Per conversazione risolta',
    'rd.activeTickets': 'Ticket attivi',
    'rd.openRightNow': 'Aperti in questo momento',
    'rd.pendingCallbacks': 'Richiamate in attesa',
    'rd.gptFallbackRate': 'Tasso di ricorso a GPT',
    'rd.totalVoiceCalls': 'Chiamate vocali totali',
    'rd.aiHandled': "Gestite dall'IA",
    'rd.aiResolved': "Risolte dall'IA",
    'rd.escalated': 'Inoltrate',
    'rd.tickets': 'Ticket',
    'rd.resolved': 'Risolti',
    'rd.avgCallDuration': 'Durata media della chiamata',
    'rd.overdueCallbacks': 'Richiamate scadute',
    'rd.callbackSlaBreaches': 'Violazioni SLA di richiamata',
    'rd.noVoiceChannelData': 'Nessun dato del canale vocale disponibile',
    'rd.totalChatSessions': 'Sessioni chat totali',
    'rd.resolutionRate': 'Tasso di risoluzione',
    'rd.avgConfidence': 'Confidenza media',
    'rd.topLanguage': 'Lingua principale',
    'rd.noChatData': 'Nessun dato di chat disponibile',
    'rd.csatScore': 'Punteggio CSAT',
    'rd.basedOnRatings': 'Basato su {count} valutazioni dei clienti',
    'rd.noCustomerRatings': 'Nessuna valutazione dei clienti ancora inviata',
    'rd.escalationCallbacks': 'Escalation e richiamate',
    'rd.reopenedTickets': 'Ticket riaperti',
    'rd.operatorEscalationDistribution': 'Distribuzione delle escalation per operatore',
    'rd.escalationsCount': '{count} escalation',
    'rd.errLoadStrategy': 'Impossibile caricare i dati di strategia',
    'rd.operationalIntelligence': 'Intelligence operativa',
    'rd.intentAnalysis': 'Analisi degli intenti',
    'rd.resolutionEscalationRate': 'Tasso di risoluzione ed escalation',
    'rd.resolvedCount': '{count} risolti',
    'rd.customerIntentDistribution': 'Distribuzione degli intenti del cliente (Chat IA)',
    'rd.noChatDataPeriod': 'Nessun dato di chat per questo periodo',
    'rd.escalationsPendingCallback': 'Escalation in attesa di richiamata',
    'rd.openaiFallbackRate': 'Tasso di ricorso a OpenAI',
    'rd.gptWhenNoMatch': 'L\'IA ha usato GPT quando la base di conoscenza non aveva corrispondenze',
    'rd.voiceTotalCalls': 'Chiamate vocali totali',
    'rd.allInboundVoice': 'Tutte le sessioni vocali IA in entrata',
    'rd.voiceAiResolved': "Voce risolta dall'IA",
    'rd.withoutHumanTransfer': 'Senza trasferimento a un umano',
    'rd.requiredReengagement': 'Ha richiesto un nuovo coinvolgimento dopo la risoluzione',
    'rd.strategicMetricsNote': 'Le metriche strategiche (NPS, fidelizzazione dei clienti, quota di mercato, previsione di abbandono, segmentazione del CLV) richiedono integrazioni di sondaggi e arricchimento dei dati CRM. Non possono essere derivate dai soli dati operativi.',
  },
  ja: {
    'rd.loadingAnalytics': '分析を読み込み中...',
    'rd.aiPerfSubtitle': 'AIパフォーマンスの分析とインサイト',
    'rd.days7': '7日間',
    'rd.days30': '30日間',
    'rd.days90': '90日間',
    'rd.exportData': 'データをエクスポート',
    'rd.errLoadAnalytics': '分析データの読み込みに失敗しました',
    'rd.aiResolutionRate': 'AI解決率',
    'rd.chatAutoResolved': 'チャット自動解決',
    'rd.avgResolutionTime': '平均解決時間',
    'rd.ticketCreateClose': 'チケット作成→クローズ',
    'rd.totalTickets': 'チケット総数',
    'rd.escalation': 'エスカレーション',
    'rd.openaiUsageRate': 'OpenAI利用率',
    'rd.aiFallbackGpt': 'AIのGPTフォールバック',
    'rd.avgFirstResponse': '平均初回応答',
    'rd.avgChatDuration': '平均チャット時間',
    'rd.callbackRate': 'コールバック率',
    'rd.reopenedConversations': '再開された会話',
    'rd.resolutionTrend': '解決トレンド',
    'rd.intentCoverage': 'インテントカバレッジ',
    'rd.forecast': '予測',
    'rd.resolutionEscalationByDay': '日別の解決率とエスカレーション率',
    'rd.resolutionRateTrend': '解決率トレンド',
    'rd.resolutionPct': '解決 %',
    'rd.escalationPct': 'エスカレーション %',
    'rd.noTicketData': 'この期間のチケットデータはありません',
    'rd.chatConversations': 'チャット会話',
    'rd.autoResolved': '自動解決',
    'rd.avgAiConfidence': '平均AI信頼度',
    'rd.solutionBankStatus': 'ソリューションバンクの状態',
    'rd.solutionBankStats': 'ソリューションバンクの統計',
    'rd.total': '合計',
    'rd.approved': '承認済み',
    'rd.pending': '保留中',
    'rd.noSolutionBankData': 'ソリューションバンクのデータはありません',
    'rd.totalSolutions': 'ソリューション総数',
    'rd.approvalRate': '承認率',
    'rd.intentDistributionChat': 'インテント分布（AIチャット）',
    'rd.intentDistribution': 'インテント分布',
    'rd.conversations': '会話',
    'rd.noIntentData': 'この期間に利用可能なインテントデータはありません',
    'rd.languageDistribution': '言語分布',
    'rd.aiPredictionsForecasts': 'AIの予測と見通し',
    'rd.aiResolutionForecast': 'AI解決予測（今後30日間）',
    'rd.currentResolutionRate': '現在の解決率',
    'rd.projectedRate5': '予測レート（5%トレンド）',
    'rd.trendPerformingWell': 'トレンド：良好なパフォーマンス',
    'rd.trendNeedsImprovement': 'トレンド：改善が必要',
    'rd.noDataYet': 'まだデータがありません',
    'rd.basedOnLiveData': '{days}日間のライブデータに基づく',
    'rd.ticketVolumeForecast': 'チケット量予測',
    'rd.currentDayVolume': '現在の{days}日間のボリューム',
    'rd.projectedMonthly': '月間予測',
    'rd.currentEscalationRate': '現在のエスカレーション率',
    'rd.basedOnLiveTicketData': 'ライブチケットデータに基づく',
    'rd.noTicketDataYet': 'まだチケットデータがありません',
    'rd.projectedFromPeriodAvg': '{days}日間の期間平均から予測',
    'rd.operationalInsights': '運用インサイト',
    'rd.solutionBankReviewNeeded': 'ソリューションバンクのレビューが必要です',
    'rd.pendingApprovalMsg': '{count}件のソリューションが承認待ちです — レビューしてAI解決率を向上させましょう',
    'rd.reopenedMsg': '{count}件の会話が再開されました — 解決の品質を確認してください',
    'rd.highCallbackQueue': 'コールバックキューが多い',
    'rd.callbackRateMsg': 'コールバック率 — オペレーターの稼働を増やすことを検討してください',
    'rd.expandSolutionBankCoverage': 'ソリューションバンクのカバレッジを拡大',
    'rd.aiResolutionLowMsg': 'AI解決率は{rate}% — 主要なインテント向けに承認済みソリューションを追加してカバレッジを向上させてください',
    'rd.strongAiPerformance': '優れたAIパフォーマンス',
    'rd.aiResolvingMsg': 'AIが会話の{rate}%を解決しています — 低信頼度のエスカレーションを確認してナレッジベースを改善することを検討してください',
    'rd.loadAnalyticsToSeeInsights': '分析データを読み込んでインサイトを表示',
    'rd.domain.conversations.name': '会話',
    'rd.domain.conversations.type': '会話履歴',
    'rd.domain.tickets.name': 'チケット',
    'rd.domain.tickets.type': 'サポートチケット',
    'rd.domain.contacts.name': '連絡先',
    'rd.domain.contacts.type': '顧客連絡先',
    'rd.domain.surveys.name': 'アンケート',
    'rd.domain.surveys.type': 'フィードバックアンケート',
    'rd.synced': '同期しました',
    'rd.syncedDesc': '{name}：{count}件のレコード。',
    'rd.syncFailed': '同期に失敗しました',
    'rd.syncing': '同期中',
    'rd.syncingDesc': 'すべてのテナントデータソースを更新中…',
    'rd.syncComplete': '同期完了',
    'rd.syncCompleteDesc': 'すべてのデータソースがライブデータで更新されました。',
    'rd.nothingToExport': 'エクスポートするものがありません',
    'rd.noRecordsDesc': '{name}にはレコードがありません。',
    'rd.source': 'ソース',
    'rd.exportReady': 'エクスポート準備完了',
    'rd.exportReadyDesc': '{name}：{count}件のレコード（{format}）。',
    'rd.csvHeaderError': 'CSVにはヘッダー行と少なくとも1件のレコードが必要です。',
    'rd.importFailedStatus': 'インポートに失敗しました（{status}）',
    'rd.importComplete': 'インポート完了',
    'rd.importCompleteDesc': '{imported}件インポート、{duplicates}件重複、{failed}件失敗。',
    'rd.importFailed': 'インポートに失敗しました',
    'rd.noWorkspace': 'アカウントはまだワークスペースにリンクされていないため、テナントデータを読み込めません。予期しない場合は管理者にお問い合わせください。',
    'rd.liveTenantData': 'ライブテナントデータ',
    'rd.importing': 'インポート中…',
    'rd.importData': 'データをインポート',
    'rd.dataFilters': 'データフィルター',
    'rd.filterDataSources': 'データソースをフィルター',
    'rd.filterDataSourcesDesc': '以下に表示されるライブテナントデータソースを絞り込みます。',
    'rd.dataSource': 'データソース',
    'rd.allSources': 'すべてのソース',
    'rd.clear': 'クリア',
    'rd.apply': '適用',
    'rd.syncAll': 'すべて同期',
    'rd.searchDataSources': 'データソースを検索...',
    'rd.dataSources': 'データソース',
    'rd.withData': 'データあり',
    'rd.totalRecords': 'レコード総数',
    'rd.avgCompleteness': '平均完全性',
    'rd.aiVoiceAgentPerformance': 'AI音声エージェントのパフォーマンス',
    'rd.loadingTenantData': 'ライブテナントデータを読み込み中…',
    'rd.noDataSourcesMatch': 'フィルターに一致するデータソースがありません。',
    'rd.failedToLoad': '読み込みに失敗しました',
    'rd.badgeError': 'エラー',
    'rd.badgeLive': 'ライブ',
    'rd.badgeEmpty': '空',
    'rd.recordsLabel': 'レコード：',
    'rd.completenessLabel': '完全性：',
    'rd.latestLabel': '最新：',
    'rd.sync': '同期',
    'rd.export': 'エクスポート',
    'rd.dataQuality': 'データ品質',
    'rd.completeness': '完全性',
    'rd.completenessNote': '完全性 = 主要フィールドが入力されたレコードの割合（ライブテナントデータから算出）。',
    'rd.details': '詳細',
    'rd.exportDetails': '詳細をエクスポート',
    'rd.downloadRecords': '{count}件のライブレコードをダウンロード。',
    'rd.latestActivity': '最新のアクティビティ：',
    'rd.channels': 'チャネル',
    'rd.syncNow': '今すぐ同期',
    'rd.errLoadStrategic': '戦略データの読み込みに失敗しました',
    'rd.operationalPerformance': '運用パフォーマンス',
    'rd.yourBusiness': 'あなたのビジネス',
    'rd.totalCustomers': '顧客総数',
    'rd.registeredInPlatform': 'プラットフォームに登録済み',
    'rd.customerCsat': '顧客CSAT',
    'rd.responsesCount': '{count}件の回答',
    'rd.noRatingsYet': 'まだ評価がありません',
    'rd.escalationRate': 'エスカレーション率',
    'rd.requiresHumanAgent': '人間のエージェントが必要',
    'rd.operationalKpis': '運用KPI',
    'rd.voiceChannel': '音声チャネル',
    'rd.chatChannel': 'チャットチャネル',
    'rd.serviceQuality': 'サービス品質',
    'rd.ticketVolumeResolutionTrend': 'チケット量と解決トレンド',
    'rd.inSelectedPeriod': '選択した期間',
    'rd.resolvedTickets': '解決済みチケット',
    'rd.closedSuccessfully': '正常にクローズ',
    'rd.reopened': '再開',
    'rd.requiredFollowup': 'フォローアップが必要',
    'rd.createToClose': '作成からクローズまで',
    'rd.firstReplyTime': '初回返信時間',
    'rd.perResolvedConversation': '解決済み会話ごと',
    'rd.activeTickets': 'アクティブなチケット',
    'rd.openRightNow': '現在オープン',
    'rd.pendingCallbacks': '保留中のコールバック',
    'rd.gptFallbackRate': 'GPTフォールバック率',
    'rd.totalVoiceCalls': '音声通話総数',
    'rd.aiHandled': 'AI処理',
    'rd.aiResolved': 'AI解決',
    'rd.escalated': 'エスカレーション済み',
    'rd.tickets': 'チケット',
    'rd.resolved': '解決済み',
    'rd.avgCallDuration': '平均通話時間',
    'rd.overdueCallbacks': '期限超過のコールバック',
    'rd.callbackSlaBreaches': 'コールバックSLA違反',
    'rd.noVoiceChannelData': '利用可能な音声チャネルデータがありません',
    'rd.totalChatSessions': 'チャットセッション総数',
    'rd.resolutionRate': '解決率',
    'rd.avgConfidence': '平均信頼度',
    'rd.topLanguage': '主要言語',
    'rd.noChatData': '利用可能なチャットデータがありません',
    'rd.csatScore': 'CSATスコア',
    'rd.basedOnRatings': '{count}件の顧客評価に基づく',
    'rd.noCustomerRatings': 'まだ顧客評価が送信されていません',
    'rd.escalationCallbacks': 'エスカレーションとコールバック',
    'rd.reopenedTickets': '再開されたチケット',
    'rd.operatorEscalationDistribution': 'オペレーター別エスカレーション分布',
    'rd.escalationsCount': '{count}件のエスカレーション',
    'rd.errLoadStrategy': '戦略データの読み込みに失敗しました',
    'rd.operationalIntelligence': '運用インテリジェンス',
    'rd.intentAnalysis': 'インテント分析',
    'rd.resolutionEscalationRate': '解決率とエスカレーション率',
    'rd.resolvedCount': '{count}件解決',
    'rd.customerIntentDistribution': '顧客インテント分布（AIチャット）',
    'rd.noChatDataPeriod': 'この期間のチャットデータはありません',
    'rd.escalationsPendingCallback': 'コールバック待ちのエスカレーション',
    'rd.openaiFallbackRate': 'OpenAIフォールバック率',
    'rd.gptWhenNoMatch': 'ナレッジベースに一致がなかったときAIがGPTを使用しました',
    'rd.voiceTotalCalls': '音声通話総数',
    'rd.allInboundVoice': 'すべての着信AI音声セッション',
    'rd.voiceAiResolved': 'AIが解決した音声',
    'rd.withoutHumanTransfer': '人間への転送なし',
    'rd.requiredReengagement': '解決後の再エンゲージメントが必要',
    'rd.strategicMetricsNote': '戦略的指標（NPS、顧客維持率、市場シェア、解約予測、CLVセグメンテーション）にはアンケート統合とCRMデータのエンリッチメントが必要です。運用データのみからは算出できません。',
  },
  ko: {
    'rd.loadingAnalytics': '분석 로딩 중...',
    'rd.aiPerfSubtitle': 'AI 성과 분석 및 인사이트',
    'rd.days7': '7일',
    'rd.days30': '30일',
    'rd.days90': '90일',
    'rd.exportData': '데이터 내보내기',
    'rd.errLoadAnalytics': '분석 데이터를 불러오지 못했습니다',
    'rd.aiResolutionRate': 'AI 해결률',
    'rd.chatAutoResolved': '채팅 자동 해결',
    'rd.avgResolutionTime': '평균 해결 시간',
    'rd.ticketCreateClose': '티켓 생성 → 종료',
    'rd.totalTickets': '전체 티켓',
    'rd.escalation': '에스컬레이션',
    'rd.openaiUsageRate': 'OpenAI 사용률',
    'rd.aiFallbackGpt': 'AI의 GPT 폴백',
    'rd.avgFirstResponse': '평균 첫 응답',
    'rd.avgChatDuration': '평균 채팅 시간',
    'rd.callbackRate': '콜백률',
    'rd.reopenedConversations': '다시 열린 대화',
    'rd.resolutionTrend': '해결 추세',
    'rd.intentCoverage': '인텐트 커버리지',
    'rd.forecast': '예측',
    'rd.resolutionEscalationByDay': '일별 해결률 및 에스컬레이션률',
    'rd.resolutionRateTrend': '해결률 추세',
    'rd.resolutionPct': '해결 %',
    'rd.escalationPct': '에스컬레이션 %',
    'rd.noTicketData': '이 기간의 티켓 데이터가 없습니다',
    'rd.chatConversations': '채팅 대화',
    'rd.autoResolved': '자동 해결됨',
    'rd.avgAiConfidence': '평균 AI 신뢰도',
    'rd.solutionBankStatus': '솔루션 뱅크 상태',
    'rd.solutionBankStats': '솔루션 뱅크 통계',
    'rd.total': '전체',
    'rd.approved': '승인됨',
    'rd.pending': '대기 중',
    'rd.noSolutionBankData': '솔루션 뱅크 데이터가 없습니다',
    'rd.totalSolutions': '전체 솔루션',
    'rd.approvalRate': '승인률',
    'rd.intentDistributionChat': '인텐트 분포 (AI 채팅)',
    'rd.intentDistribution': '인텐트 분포',
    'rd.conversations': '대화',
    'rd.noIntentData': '이 기간에 사용 가능한 인텐트 데이터가 없습니다',
    'rd.languageDistribution': '언어 분포',
    'rd.aiPredictionsForecasts': 'AI 예측 및 전망',
    'rd.aiResolutionForecast': 'AI 해결 예측 (향후 30일)',
    'rd.currentResolutionRate': '현재 해결률',
    'rd.projectedRate5': '예상 비율 (5% 추세)',
    'rd.trendPerformingWell': '추세: 우수한 성과',
    'rd.trendNeedsImprovement': '추세: 개선 필요',
    'rd.noDataYet': '아직 데이터가 없습니다',
    'rd.basedOnLiveData': '{days}일간의 실시간 데이터 기반',
    'rd.ticketVolumeForecast': '티켓 볼륨 예측',
    'rd.currentDayVolume': '현재 {days}일 볼륨',
    'rd.projectedMonthly': '월간 예상',
    'rd.currentEscalationRate': '현재 에스컬레이션률',
    'rd.basedOnLiveTicketData': '실시간 티켓 데이터 기반',
    'rd.noTicketDataYet': '아직 티켓 데이터가 없습니다',
    'rd.projectedFromPeriodAvg': '{days}일 기간 평균에서 예측',
    'rd.operationalInsights': '운영 인사이트',
    'rd.solutionBankReviewNeeded': '솔루션 뱅크 검토 필요',
    'rd.pendingApprovalMsg': '{count}개의 솔루션이 승인 대기 중입니다 — 검토하여 AI 해결률을 개선하세요',
    'rd.reopenedMsg': '{count}개의 대화가 다시 열렸습니다 — 해결 품질을 검토하세요',
    'rd.highCallbackQueue': '높은 콜백 대기열',
    'rd.callbackRateMsg': '콜백률 — 운영자 가용성 확대를 고려하세요',
    'rd.expandSolutionBankCoverage': '솔루션 뱅크 커버리지 확대',
    'rd.aiResolutionLowMsg': 'AI 해결률 {rate}% — 주요 인텐트에 대한 승인된 솔루션을 추가하여 커버리지를 개선하세요',
    'rd.strongAiPerformance': '강력한 AI 성과',
    'rd.aiResolvingMsg': 'AI가 대화의 {rate}%를 해결하고 있습니다 — 낮은 신뢰도의 에스컬레이션을 검토하여 지식 베이스를 개선하는 것을 고려하세요',
    'rd.loadAnalyticsToSeeInsights': '분석 데이터를 불러와 인사이트를 확인하세요',
    'rd.domain.conversations.name': '대화',
    'rd.domain.conversations.type': '대화 기록',
    'rd.domain.tickets.name': '티켓',
    'rd.domain.tickets.type': '지원 티켓',
    'rd.domain.contacts.name': '연락처',
    'rd.domain.contacts.type': '고객 연락처',
    'rd.domain.surveys.name': '설문',
    'rd.domain.surveys.type': '피드백 설문',
    'rd.synced': '동기화됨',
    'rd.syncedDesc': '{name}: 레코드 {count}개.',
    'rd.syncFailed': '동기화 실패',
    'rd.syncing': '동기화 중',
    'rd.syncingDesc': '모든 테넌트 데이터 소스를 새로 고치는 중…',
    'rd.syncComplete': '동기화 완료',
    'rd.syncCompleteDesc': '모든 데이터 소스가 실시간 데이터로 새로 고쳐졌습니다.',
    'rd.nothingToExport': '내보낼 항목이 없습니다',
    'rd.noRecordsDesc': '{name}에 레코드가 없습니다.',
    'rd.source': '소스',
    'rd.exportReady': '내보내기 준비 완료',
    'rd.exportReadyDesc': '{name}: 레코드 {count}개 ({format}).',
    'rd.csvHeaderError': 'CSV에는 헤더 행과 최소 한 개의 레코드가 있어야 합니다.',
    'rd.importFailedStatus': '가져오기 실패 ({status})',
    'rd.importComplete': '가져오기 완료',
    'rd.importCompleteDesc': '{imported}개 가져옴, {duplicates}개 중복, {failed}개 실패.',
    'rd.importFailed': '가져오기 실패',
    'rd.noWorkspace': '계정이 아직 워크스페이스에 연결되지 않아 테넌트 데이터를 불러올 수 없습니다. 예상치 못한 경우 관리자에게 문의하세요.',
    'rd.liveTenantData': '실시간 테넌트 데이터',
    'rd.importing': '가져오는 중…',
    'rd.importData': '데이터 가져오기',
    'rd.dataFilters': '데이터 필터',
    'rd.filterDataSources': '데이터 소스 필터링',
    'rd.filterDataSourcesDesc': '아래에 표시된 실시간 테넌트 데이터 소스를 좁힙니다.',
    'rd.dataSource': '데이터 소스',
    'rd.allSources': '모든 소스',
    'rd.clear': '지우기',
    'rd.apply': '적용',
    'rd.syncAll': '모두 동기화',
    'rd.searchDataSources': '데이터 소스 검색...',
    'rd.dataSources': '데이터 소스',
    'rd.withData': '데이터 있음',
    'rd.totalRecords': '전체 레코드',
    'rd.avgCompleteness': '평균 완전성',
    'rd.aiVoiceAgentPerformance': 'AI 음성 상담원 성과',
    'rd.loadingTenantData': '실시간 테넌트 데이터를 불러오는 중…',
    'rd.noDataSourcesMatch': '필터와 일치하는 데이터 소스가 없습니다.',
    'rd.failedToLoad': '불러오기 실패',
    'rd.badgeError': '오류',
    'rd.badgeLive': '실시간',
    'rd.badgeEmpty': '비어 있음',
    'rd.recordsLabel': '레코드:',
    'rd.completenessLabel': '완전성:',
    'rd.latestLabel': '최신:',
    'rd.sync': '동기화',
    'rd.export': '내보내기',
    'rd.dataQuality': '데이터 품질',
    'rd.completeness': '완전성',
    'rd.completenessNote': '완전성 = 주요 필드가 채워진 레코드의 비율 (실시간 테넌트 데이터에서 도출).',
    'rd.details': '세부 정보',
    'rd.exportDetails': '세부 정보 내보내기',
    'rd.downloadRecords': '실시간 레코드 {count}개 다운로드.',
    'rd.latestActivity': '최근 활동:',
    'rd.channels': '채널',
    'rd.syncNow': '지금 동기화',
    'rd.errLoadStrategic': '전략 데이터를 불러오지 못했습니다',
    'rd.operationalPerformance': '운영 성과',
    'rd.yourBusiness': '내 비즈니스',
    'rd.totalCustomers': '전체 고객',
    'rd.registeredInPlatform': '플랫폼에 등록됨',
    'rd.customerCsat': '고객 CSAT',
    'rd.responsesCount': '응답 {count}개',
    'rd.noRatingsYet': '아직 평가가 없습니다',
    'rd.escalationRate': '에스컬레이션률',
    'rd.requiresHumanAgent': '상담원 필요',
    'rd.operationalKpis': '운영 KPI',
    'rd.voiceChannel': '음성 채널',
    'rd.chatChannel': '채팅 채널',
    'rd.serviceQuality': '서비스 품질',
    'rd.ticketVolumeResolutionTrend': '티켓 볼륨 및 해결 추세',
    'rd.inSelectedPeriod': '선택한 기간',
    'rd.resolvedTickets': '해결된 티켓',
    'rd.closedSuccessfully': '성공적으로 종료됨',
    'rd.reopened': '다시 열림',
    'rd.requiredFollowup': '후속 조치 필요',
    'rd.createToClose': '생성부터 종료까지',
    'rd.firstReplyTime': '첫 응답 시간',
    'rd.perResolvedConversation': '해결된 대화당',
    'rd.activeTickets': '활성 티켓',
    'rd.openRightNow': '현재 열림',
    'rd.pendingCallbacks': '대기 중인 콜백',
    'rd.gptFallbackRate': 'GPT 폴백률',
    'rd.totalVoiceCalls': '전체 음성 통화',
    'rd.aiHandled': 'AI 처리',
    'rd.aiResolved': 'AI 해결',
    'rd.escalated': '에스컬레이션됨',
    'rd.tickets': '티켓',
    'rd.resolved': '해결됨',
    'rd.avgCallDuration': '평균 통화 시간',
    'rd.overdueCallbacks': '기한 초과 콜백',
    'rd.callbackSlaBreaches': '콜백 SLA 위반',
    'rd.noVoiceChannelData': '사용 가능한 음성 채널 데이터가 없습니다',
    'rd.totalChatSessions': '전체 채팅 세션',
    'rd.resolutionRate': '해결률',
    'rd.avgConfidence': '평균 신뢰도',
    'rd.topLanguage': '주요 언어',
    'rd.noChatData': '사용 가능한 채팅 데이터가 없습니다',
    'rd.csatScore': 'CSAT 점수',
    'rd.basedOnRatings': '{count}개의 고객 평가 기반',
    'rd.noCustomerRatings': '아직 제출된 고객 평가가 없습니다',
    'rd.escalationCallbacks': '에스컬레이션 및 콜백',
    'rd.reopenedTickets': '다시 열린 티켓',
    'rd.operatorEscalationDistribution': '운영자별 에스컬레이션 분포',
    'rd.escalationsCount': '에스컬레이션 {count}건',
    'rd.errLoadStrategy': '전략 데이터를 불러오지 못했습니다',
    'rd.operationalIntelligence': '운영 인텔리전스',
    'rd.intentAnalysis': '인텐트 분석',
    'rd.resolutionEscalationRate': '해결률 및 에스컬레이션률',
    'rd.resolvedCount': '{count}개 해결',
    'rd.customerIntentDistribution': '고객 인텐트 분포 (AI 채팅)',
    'rd.noChatDataPeriod': '이 기간의 채팅 데이터가 없습니다',
    'rd.escalationsPendingCallback': '콜백 대기 중인 에스컬레이션',
    'rd.openaiFallbackRate': 'OpenAI 폴백률',
    'rd.gptWhenNoMatch': '지식 베이스에 일치 항목이 없을 때 AI가 GPT를 사용했습니다',
    'rd.voiceTotalCalls': '전체 음성 통화',
    'rd.allInboundVoice': '모든 인바운드 AI 음성 세션',
    'rd.voiceAiResolved': 'AI가 해결한 음성',
    'rd.withoutHumanTransfer': '상담원 전환 없이',
    'rd.requiredReengagement': '해결 후 재참여 필요',
    'rd.strategicMetricsNote': '전략적 지표(NPS, 고객 유지율, 시장 점유율, 이탈 예측, CLV 세분화)에는 설문 통합과 CRM 데이터 보강이 필요합니다. 운영 데이터만으로는 도출할 수 없습니다.',
  },
  zh: {
    'rd.loadingAnalytics': '正在加载分析...',
    'rd.aiPerfSubtitle': 'AI 性能分析与洞察',
    'rd.days7': '7 天',
    'rd.days30': '30 天',
    'rd.days90': '90 天',
    'rd.exportData': '导出数据',
    'rd.errLoadAnalytics': '加载分析数据失败',
    'rd.aiResolutionRate': 'AI 解决率',
    'rd.chatAutoResolved': '聊天自动解决',
    'rd.avgResolutionTime': '平均解决时间',
    'rd.ticketCreateClose': '工单创建 → 关闭',
    'rd.totalTickets': '工单总数',
    'rd.escalation': '升级',
    'rd.openaiUsageRate': 'OpenAI 使用率',
    'rd.aiFallbackGpt': 'AI 回退到 GPT',
    'rd.avgFirstResponse': '平均首次响应',
    'rd.avgChatDuration': '平均聊天时长',
    'rd.callbackRate': '回拨率',
    'rd.reopenedConversations': '重新打开的对话',
    'rd.resolutionTrend': '解决趋势',
    'rd.intentCoverage': '意图覆盖率',
    'rd.forecast': '预测',
    'rd.resolutionEscalationByDay': '按天的解决率和升级率',
    'rd.resolutionRateTrend': '解决率趋势',
    'rd.resolutionPct': '解决 %',
    'rd.escalationPct': '升级 %',
    'rd.noTicketData': '此期间没有工单数据',
    'rd.chatConversations': '聊天对话',
    'rd.autoResolved': '自动解决',
    'rd.avgAiConfidence': '平均 AI 置信度',
    'rd.solutionBankStatus': '解决方案库状态',
    'rd.solutionBankStats': '解决方案库统计',
    'rd.total': '总计',
    'rd.approved': '已批准',
    'rd.pending': '待处理',
    'rd.noSolutionBankData': '没有解决方案库数据',
    'rd.totalSolutions': '解决方案总数',
    'rd.approvalRate': '批准率',
    'rd.intentDistributionChat': '意图分布（AI 聊天）',
    'rd.intentDistribution': '意图分布',
    'rd.conversations': '对话',
    'rd.noIntentData': '此期间没有可用的意图数据',
    'rd.languageDistribution': '语言分布',
    'rd.aiPredictionsForecasts': 'AI 预测与预报',
    'rd.aiResolutionForecast': 'AI 解决预测（未来 30 天）',
    'rd.currentResolutionRate': '当前解决率',
    'rd.projectedRate5': '预计比率（5% 趋势）',
    'rd.trendPerformingWell': '趋势：表现良好',
    'rd.trendNeedsImprovement': '趋势：需要改进',
    'rd.noDataYet': '暂无数据',
    'rd.basedOnLiveData': '基于 {days} 天的实时数据',
    'rd.ticketVolumeForecast': '工单量预测',
    'rd.currentDayVolume': '当前 {days} 天的量',
    'rd.projectedMonthly': '月度预测',
    'rd.currentEscalationRate': '当前升级率',
    'rd.basedOnLiveTicketData': '基于实时工单数据',
    'rd.noTicketDataYet': '暂无工单数据',
    'rd.projectedFromPeriodAvg': '根据 {days} 天期间平均值预测',
    'rd.operationalInsights': '运营洞察',
    'rd.solutionBankReviewNeeded': '需要审核解决方案库',
    'rd.pendingApprovalMsg': '{count} 个解决方案待批准 — 请审核以提高 AI 解决率',
    'rd.reopenedMsg': '{count} 个对话已重新打开 — 请审核解决质量',
    'rd.highCallbackQueue': '回拨队列过高',
    'rd.callbackRateMsg': '回拨率 — 请考虑增加操作员可用性',
    'rd.expandSolutionBankCoverage': '扩展解决方案库覆盖范围',
    'rd.aiResolutionLowMsg': 'AI 解决率为 {rate}% — 为主要意图添加已批准的解决方案以提高覆盖率',
    'rd.strongAiPerformance': 'AI 表现强劲',
    'rd.aiResolvingMsg': 'AI 正在解决 {rate}% 的对话 — 请考虑审核低置信度升级以改进知识库',
    'rd.loadAnalyticsToSeeInsights': '加载分析数据以查看洞察',
    'rd.domain.conversations.name': '对话',
    'rd.domain.conversations.type': '对话历史',
    'rd.domain.tickets.name': '工单',
    'rd.domain.tickets.type': '支持工单',
    'rd.domain.contacts.name': '联系人',
    'rd.domain.contacts.type': '客户联系人',
    'rd.domain.surveys.name': '调查',
    'rd.domain.surveys.type': '反馈调查',
    'rd.synced': '已同步',
    'rd.syncedDesc': '{name}：{count} 条记录。',
    'rd.syncFailed': '同步失败',
    'rd.syncing': '正在同步',
    'rd.syncingDesc': '正在刷新所有租户数据源…',
    'rd.syncComplete': '同步完成',
    'rd.syncCompleteDesc': '所有数据源已使用实时数据刷新。',
    'rd.nothingToExport': '没有可导出的内容',
    'rd.noRecordsDesc': '{name} 没有记录。',
    'rd.source': '来源',
    'rd.exportReady': '导出就绪',
    'rd.exportReadyDesc': '{name}：{count} 条记录（{format}）。',
    'rd.csvHeaderError': 'CSV 必须包含标题行和至少一条记录。',
    'rd.importFailedStatus': '导入失败（{status}）',
    'rd.importComplete': '导入完成',
    'rd.importCompleteDesc': '已导入 {imported} 条，{duplicates} 条重复，{failed} 条失败。',
    'rd.importFailed': '导入失败',
    'rd.noWorkspace': '您的账户尚未关联到工作区，因此无法加载租户数据。如果这不符合预期，请联系您的管理员。',
    'rd.liveTenantData': '实时租户数据',
    'rd.importing': '正在导入…',
    'rd.importData': '导入数据',
    'rd.dataFilters': '数据筛选',
    'rd.filterDataSources': '筛选数据源',
    'rd.filterDataSourcesDesc': '缩小下方显示的实时租户数据源范围。',
    'rd.dataSource': '数据源',
    'rd.allSources': '所有来源',
    'rd.clear': '清除',
    'rd.apply': '应用',
    'rd.syncAll': '全部同步',
    'rd.searchDataSources': '搜索数据源...',
    'rd.dataSources': '数据源',
    'rd.withData': '有数据',
    'rd.totalRecords': '记录总数',
    'rd.avgCompleteness': '平均完整度',
    'rd.aiVoiceAgentPerformance': 'AI 语音客服性能',
    'rd.loadingTenantData': '正在加载实时租户数据…',
    'rd.noDataSourcesMatch': '没有数据源与您的筛选条件匹配。',
    'rd.failedToLoad': '加载失败',
    'rd.badgeError': '错误',
    'rd.badgeLive': '实时',
    'rd.badgeEmpty': '空',
    'rd.recordsLabel': '记录：',
    'rd.completenessLabel': '完整度：',
    'rd.latestLabel': '最新：',
    'rd.sync': '同步',
    'rd.export': '导出',
    'rd.dataQuality': '数据质量',
    'rd.completeness': '完整度',
    'rd.completenessNote': '完整度 = 关键字段已填充的记录占比（源自实时租户数据）。',
    'rd.details': '详情',
    'rd.exportDetails': '导出详情',
    'rd.downloadRecords': '下载 {count} 条实时记录。',
    'rd.latestActivity': '最近活动：',
    'rd.channels': '渠道',
    'rd.syncNow': '立即同步',
    'rd.errLoadStrategic': '加载战略数据失败',
    'rd.operationalPerformance': '运营绩效',
    'rd.yourBusiness': '您的企业',
    'rd.totalCustomers': '客户总数',
    'rd.registeredInPlatform': '已在平台注册',
    'rd.customerCsat': '客户 CSAT',
    'rd.responsesCount': '{count} 条回复',
    'rd.noRatingsYet': '暂无评分',
    'rd.escalationRate': '升级率',
    'rd.requiresHumanAgent': '需要人工客服',
    'rd.operationalKpis': '运营 KPI',
    'rd.voiceChannel': '语音渠道',
    'rd.chatChannel': '聊天渠道',
    'rd.serviceQuality': '服务质量',
    'rd.ticketVolumeResolutionTrend': '工单量与解决趋势',
    'rd.inSelectedPeriod': '在所选期间',
    'rd.resolvedTickets': '已解决工单',
    'rd.closedSuccessfully': '成功关闭',
    'rd.reopened': '重新打开',
    'rd.requiredFollowup': '需要跟进',
    'rd.createToClose': '从创建到关闭',
    'rd.firstReplyTime': '首次回复时间',
    'rd.perResolvedConversation': '每个已解决对话',
    'rd.activeTickets': '活动工单',
    'rd.openRightNow': '当前打开',
    'rd.pendingCallbacks': '待处理回拨',
    'rd.gptFallbackRate': 'GPT 回退率',
    'rd.totalVoiceCalls': '语音通话总数',
    'rd.aiHandled': 'AI 处理',
    'rd.aiResolved': 'AI 解决',
    'rd.escalated': '已升级',
    'rd.tickets': '工单',
    'rd.resolved': '已解决',
    'rd.avgCallDuration': '平均通话时长',
    'rd.overdueCallbacks': '逾期回拨',
    'rd.callbackSlaBreaches': '回拨 SLA 违规',
    'rd.noVoiceChannelData': '没有可用的语音渠道数据',
    'rd.totalChatSessions': '聊天会话总数',
    'rd.resolutionRate': '解决率',
    'rd.avgConfidence': '平均置信度',
    'rd.topLanguage': '主要语言',
    'rd.noChatData': '没有可用的聊天数据',
    'rd.csatScore': 'CSAT 分数',
    'rd.basedOnRatings': '基于 {count} 条客户评分',
    'rd.noCustomerRatings': '尚未提交客户评分',
    'rd.escalationCallbacks': '升级与回拨',
    'rd.reopenedTickets': '重新打开的工单',
    'rd.operatorEscalationDistribution': '按操作员的升级分布',
    'rd.escalationsCount': '{count} 次升级',
    'rd.errLoadStrategy': '加载战略数据失败',
    'rd.operationalIntelligence': '运营智能',
    'rd.intentAnalysis': '意图分析',
    'rd.resolutionEscalationRate': '解决率与升级率',
    'rd.resolvedCount': '{count} 个已解决',
    'rd.customerIntentDistribution': '客户意图分布（AI 聊天）',
    'rd.noChatDataPeriod': '此期间没有聊天数据',
    'rd.escalationsPendingCallback': '待回拨的升级',
    'rd.openaiFallbackRate': 'OpenAI 回退率',
    'rd.gptWhenNoMatch': '当知识库没有匹配时 AI 使用了 GPT',
    'rd.voiceTotalCalls': '语音通话总数',
    'rd.allInboundVoice': '所有入站 AI 语音会话',
    'rd.voiceAiResolved': 'AI 解决的语音',
    'rd.withoutHumanTransfer': '无需人工转接',
    'rd.requiredReengagement': '解决后需要重新互动',
    'rd.strategicMetricsNote': '战略指标（NPS、客户留存、市场份额、流失预测、CLV 细分）需要调查集成和 CRM 数据丰富。这些无法仅从运营数据中得出。',
  },
  ar: {
    'rd.loadingAnalytics': 'جارٍ تحميل التحليلات...',
    'rd.aiPerfSubtitle': 'تحليلات ورؤى أداء الذكاء الاصطناعي',
    'rd.days7': '7 أيام',
    'rd.days30': '30 يومًا',
    'rd.days90': '90 يومًا',
    'rd.exportData': 'تصدير البيانات',
    'rd.errLoadAnalytics': 'فشل تحميل بيانات التحليلات',
    'rd.aiResolutionRate': 'معدل الحل بالذكاء الاصطناعي',
    'rd.chatAutoResolved': 'دردشة محلولة تلقائيًا',
    'rd.avgResolutionTime': 'متوسط وقت الحل',
    'rd.ticketCreateClose': 'إنشاء التذكرة ← إغلاقها',
    'rd.totalTickets': 'إجمالي التذاكر',
    'rd.escalation': 'التصعيد',
    'rd.openaiUsageRate': 'معدل استخدام OpenAI',
    'rd.aiFallbackGpt': 'رجوع الذكاء الاصطناعي إلى GPT',
    'rd.avgFirstResponse': 'متوسط أول استجابة',
    'rd.avgChatDuration': 'متوسط مدة الدردشة',
    'rd.callbackRate': 'معدل معاودة الاتصال',
    'rd.reopenedConversations': 'محادثات أُعيد فتحها',
    'rd.resolutionTrend': 'اتجاه الحل',
    'rd.intentCoverage': 'تغطية النية',
    'rd.forecast': 'التوقعات',
    'rd.resolutionEscalationByDay': 'معدل الحل والتصعيد حسب اليوم',
    'rd.resolutionRateTrend': 'اتجاه معدل الحل',
    'rd.resolutionPct': 'الحل %',
    'rd.escalationPct': 'التصعيد %',
    'rd.noTicketData': 'لا توجد بيانات تذاكر لهذه الفترة',
    'rd.chatConversations': 'محادثات الدردشة',
    'rd.autoResolved': 'محلول تلقائيًا',
    'rd.avgAiConfidence': 'متوسط ثقة الذكاء الاصطناعي',
    'rd.solutionBankStatus': 'حالة بنك الحلول',
    'rd.solutionBankStats': 'إحصائيات بنك الحلول',
    'rd.total': 'الإجمالي',
    'rd.approved': 'معتمد',
    'rd.pending': 'قيد الانتظار',
    'rd.noSolutionBankData': 'لا توجد بيانات لبنك الحلول',
    'rd.totalSolutions': 'إجمالي الحلول',
    'rd.approvalRate': 'معدل الاعتماد',
    'rd.intentDistributionChat': 'توزيع النية (دردشة الذكاء الاصطناعي)',
    'rd.intentDistribution': 'توزيع النية',
    'rd.conversations': 'المحادثات',
    'rd.noIntentData': 'لا توجد بيانات نية متاحة لهذه الفترة',
    'rd.languageDistribution': 'توزيع اللغات',
    'rd.aiPredictionsForecasts': 'تنبؤات وتوقعات الذكاء الاصطناعي',
    'rd.aiResolutionForecast': 'توقعات الحل بالذكاء الاصطناعي (الـ 30 يومًا القادمة)',
    'rd.currentResolutionRate': 'معدل الحل الحالي',
    'rd.projectedRate5': 'المعدل المتوقع (اتجاه 5%)',
    'rd.trendPerformingWell': 'الاتجاه: أداء جيد',
    'rd.trendNeedsImprovement': 'الاتجاه: يحتاج إلى تحسين',
    'rd.noDataYet': 'لا توجد بيانات بعد',
    'rd.basedOnLiveData': 'بناءً على بيانات مباشرة لـ {days} يومًا',
    'rd.ticketVolumeForecast': 'توقعات حجم التذاكر',
    'rd.currentDayVolume': 'الحجم الحالي لـ {days} يومًا',
    'rd.projectedMonthly': 'التوقع الشهري',
    'rd.currentEscalationRate': 'معدل التصعيد الحالي',
    'rd.basedOnLiveTicketData': 'بناءً على بيانات التذاكر المباشرة',
    'rd.noTicketDataYet': 'لا توجد بيانات تذاكر بعد',
    'rd.projectedFromPeriodAvg': 'متوقع من متوسط فترة الـ {days} يومًا',
    'rd.operationalInsights': 'رؤى تشغيلية',
    'rd.solutionBankReviewNeeded': 'مطلوب مراجعة بنك الحلول',
    'rd.pendingApprovalMsg': '{count} حل بانتظار الاعتماد — راجعها لتحسين معدل الحل بالذكاء الاصطناعي',
    'rd.reopenedMsg': '{count} محادثة أُعيد فتحها — راجع جودة الحل',
    'rd.highCallbackQueue': 'قائمة انتظار معاودة اتصال مرتفعة',
    'rd.callbackRateMsg': 'معدل معاودة الاتصال — فكّر في زيادة توفر المشغلين',
    'rd.expandSolutionBankCoverage': 'توسيع تغطية بنك الحلول',
    'rd.aiResolutionLowMsg': 'الحل بالذكاء الاصطناعي عند {rate}% — أضف حلولًا معتمدة لأهم النوايا لتحسين التغطية',
    'rd.strongAiPerformance': 'أداء قوي للذكاء الاصطناعي',
    'rd.aiResolvingMsg': 'يحل الذكاء الاصطناعي {rate}% من المحادثات — فكّر في مراجعة التصعيدات منخفضة الثقة لتحسين قاعدة المعرفة',
    'rd.loadAnalyticsToSeeInsights': 'حمّل بيانات التحليلات لعرض الرؤى',
    'rd.domain.conversations.name': 'المحادثات',
    'rd.domain.conversations.type': 'سجل المحادثات',
    'rd.domain.tickets.name': 'التذاكر',
    'rd.domain.tickets.type': 'تذاكر الدعم',
    'rd.domain.contacts.name': 'جهات الاتصال',
    'rd.domain.contacts.type': 'جهات اتصال العملاء',
    'rd.domain.surveys.name': 'الاستطلاعات',
    'rd.domain.surveys.type': 'استطلاعات الملاحظات',
    'rd.synced': 'تمت المزامنة',
    'rd.syncedDesc': '{name}: {count} سجل.',
    'rd.syncFailed': 'فشلت المزامنة',
    'rd.syncing': 'جارٍ المزامنة',
    'rd.syncingDesc': 'جارٍ تحديث جميع مصادر بيانات المستأجر…',
    'rd.syncComplete': 'اكتملت المزامنة',
    'rd.syncCompleteDesc': 'تم تحديث جميع مصادر البيانات ببيانات مباشرة.',
    'rd.nothingToExport': 'لا شيء للتصدير',
    'rd.noRecordsDesc': '{name} لا يحتوي على سجلات.',
    'rd.source': 'المصدر',
    'rd.exportReady': 'التصدير جاهز',
    'rd.exportReadyDesc': '{name}: {count} سجل ({format}).',
    'rd.csvHeaderError': 'يجب أن يحتوي ملف CSV على صف عناوين وسجل واحد على الأقل.',
    'rd.importFailedStatus': 'فشل الاستيراد ({status})',
    'rd.importComplete': 'اكتمل الاستيراد',
    'rd.importCompleteDesc': '{imported} مستورد، {duplicates} مكرر، {failed} فاشل.',
    'rd.importFailed': 'فشل الاستيراد',
    'rd.noWorkspace': 'حسابك غير مرتبط بمساحة عمل بعد، لذا لا يمكن تحميل بيانات المستأجر. تواصل مع المسؤول إذا كان هذا غير متوقع.',
    'rd.liveTenantData': 'بيانات المستأجر المباشرة',
    'rd.importing': 'جارٍ الاستيراد…',
    'rd.importData': 'استيراد البيانات',
    'rd.dataFilters': 'مرشحات البيانات',
    'rd.filterDataSources': 'تصفية مصادر البيانات',
    'rd.filterDataSourcesDesc': 'قلّص مصادر بيانات المستأجر المباشرة المعروضة أدناه.',
    'rd.dataSource': 'مصدر البيانات',
    'rd.allSources': 'جميع المصادر',
    'rd.clear': 'مسح',
    'rd.apply': 'تطبيق',
    'rd.syncAll': 'مزامنة الكل',
    'rd.searchDataSources': 'البحث في مصادر البيانات...',
    'rd.dataSources': 'مصادر البيانات',
    'rd.withData': 'تحتوي على بيانات',
    'rd.totalRecords': 'إجمالي السجلات',
    'rd.avgCompleteness': 'متوسط الاكتمال',
    'rd.aiVoiceAgentPerformance': 'أداء وكيل الصوت بالذكاء الاصطناعي',
    'rd.loadingTenantData': 'جارٍ تحميل بيانات المستأجر المباشرة…',
    'rd.noDataSourcesMatch': 'لا توجد مصادر بيانات تطابق مرشحك.',
    'rd.failedToLoad': 'فشل التحميل',
    'rd.badgeError': 'خطأ',
    'rd.badgeLive': 'مباشر',
    'rd.badgeEmpty': 'فارغ',
    'rd.recordsLabel': 'السجلات:',
    'rd.completenessLabel': 'الاكتمال:',
    'rd.latestLabel': 'الأحدث:',
    'rd.sync': 'مزامنة',
    'rd.export': 'تصدير',
    'rd.dataQuality': 'جودة البيانات',
    'rd.completeness': 'الاكتمال',
    'rd.completenessNote': 'الاكتمال = نسبة السجلات التي تحتوي على الحقول الرئيسية معبّأة (مشتقة من بيانات المستأجر المباشرة).',
    'rd.details': 'التفاصيل',
    'rd.exportDetails': 'تصدير التفاصيل',
    'rd.downloadRecords': 'تنزيل {count} سجل مباشر.',
    'rd.latestActivity': 'آخر نشاط:',
    'rd.channels': 'القنوات',
    'rd.syncNow': 'مزامنة الآن',
    'rd.errLoadStrategic': 'فشل تحميل البيانات الاستراتيجية',
    'rd.operationalPerformance': 'الأداء التشغيلي',
    'rd.yourBusiness': 'عملك',
    'rd.totalCustomers': 'إجمالي العملاء',
    'rd.registeredInPlatform': 'مسجلون في المنصة',
    'rd.customerCsat': 'رضا العملاء CSAT',
    'rd.responsesCount': '{count} رد',
    'rd.noRatingsYet': 'لا توجد تقييمات بعد',
    'rd.escalationRate': 'معدل التصعيد',
    'rd.requiresHumanAgent': 'يتطلب وكيلًا بشريًا',
    'rd.operationalKpis': 'مؤشرات الأداء التشغيلية',
    'rd.voiceChannel': 'قناة الصوت',
    'rd.chatChannel': 'قناة الدردشة',
    'rd.serviceQuality': 'جودة الخدمة',
    'rd.ticketVolumeResolutionTrend': 'حجم التذاكر واتجاه الحل',
    'rd.inSelectedPeriod': 'في الفترة المحددة',
    'rd.resolvedTickets': 'التذاكر المحلولة',
    'rd.closedSuccessfully': 'أُغلقت بنجاح',
    'rd.reopened': 'أُعيد فتحها',
    'rd.requiredFollowup': 'تطلبت متابعة',
    'rd.createToClose': 'من الإنشاء إلى الإغلاق',
    'rd.firstReplyTime': 'وقت أول رد',
    'rd.perResolvedConversation': 'لكل محادثة محلولة',
    'rd.activeTickets': 'التذاكر النشطة',
    'rd.openRightNow': 'مفتوحة الآن',
    'rd.pendingCallbacks': 'معاودات اتصال معلّقة',
    'rd.gptFallbackRate': 'معدل الرجوع إلى GPT',
    'rd.totalVoiceCalls': 'إجمالي المكالمات الصوتية',
    'rd.aiHandled': 'عالجها الذكاء الاصطناعي',
    'rd.aiResolved': 'حلّها الذكاء الاصطناعي',
    'rd.escalated': 'مُصعّدة',
    'rd.tickets': 'التذاكر',
    'rd.resolved': 'محلولة',
    'rd.avgCallDuration': 'متوسط مدة المكالمة',
    'rd.overdueCallbacks': 'معاودات اتصال متأخرة',
    'rd.callbackSlaBreaches': 'انتهاكات اتفاقية مستوى خدمة معاودة الاتصال',
    'rd.noVoiceChannelData': 'لا توجد بيانات قناة صوت متاحة',
    'rd.totalChatSessions': 'إجمالي جلسات الدردشة',
    'rd.resolutionRate': 'معدل الحل',
    'rd.avgConfidence': 'متوسط الثقة',
    'rd.topLanguage': 'اللغة الأكثر استخدامًا',
    'rd.noChatData': 'لا توجد بيانات دردشة متاحة',
    'rd.csatScore': 'درجة CSAT',
    'rd.basedOnRatings': 'بناءً على {count} تقييم للعملاء',
    'rd.noCustomerRatings': 'لم يتم إرسال أي تقييمات للعملاء بعد',
    'rd.escalationCallbacks': 'التصعيدات ومعاودات الاتصال',
    'rd.reopenedTickets': 'التذاكر التي أُعيد فتحها',
    'rd.operatorEscalationDistribution': 'توزيع التصعيدات حسب المشغل',
    'rd.escalationsCount': '{count} تصعيد',
    'rd.errLoadStrategy': 'فشل تحميل بيانات الاستراتيجية',
    'rd.operationalIntelligence': 'الذكاء التشغيلي',
    'rd.intentAnalysis': 'تحليل النية',
    'rd.resolutionEscalationRate': 'معدل الحل والتصعيد',
    'rd.resolvedCount': '{count} محلول',
    'rd.customerIntentDistribution': 'توزيع نية العميل (دردشة الذكاء الاصطناعي)',
    'rd.noChatDataPeriod': 'لا توجد بيانات دردشة لهذه الفترة',
    'rd.escalationsPendingCallback': 'تصعيدات بانتظار معاودة الاتصال',
    'rd.openaiFallbackRate': 'معدل الرجوع إلى OpenAI',
    'rd.gptWhenNoMatch': 'استخدم الذكاء الاصطناعي GPT عندما لم تجد قاعدة المعرفة تطابقًا',
    'rd.voiceTotalCalls': 'إجمالي المكالمات الصوتية',
    'rd.allInboundVoice': 'جميع جلسات الصوت الواردة بالذكاء الاصطناعي',
    'rd.voiceAiResolved': 'صوت حلّه الذكاء الاصطناعي',
    'rd.withoutHumanTransfer': 'دون تحويل إلى بشري',
    'rd.requiredReengagement': 'تطلبت إعادة تفاعل بعد الحل',
    'rd.strategicMetricsNote': 'تتطلب المقاييس الاستراتيجية (NPS، والاحتفاظ بالعملاء، وحصة السوق، والتنبؤ بالتسرب، وتقسيم CLV) تكاملات استطلاعات وإثراء بيانات CRM. لا يمكن اشتقاقها من البيانات التشغيلية وحدها.',
  },
};

export default roleDashTranslations;
